import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, useBlocker } from 'react-router-dom';
import { useIntl } from 'react-intl';

// material-ui
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme, alpha } from '@mui/material/styles';

// ant design icons
import LeftOutlined from '@ant-design/icons/LeftOutlined';
import FileTextOutlined from '@ant-design/icons/FileTextOutlined';
import SaveOutlined from '@ant-design/icons/SaveOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import EditOutlined from '@ant-design/icons/EditOutlined';
import DownOutlined from '@ant-design/icons/DownOutlined';
import UpOutlined from '@ant-design/icons/UpOutlined';
import FolderOpenOutlined from '@ant-design/icons/FolderOpenOutlined';
import FilePdfOutlined from '@ant-design/icons/FilePdfOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import EyeOutlined from '@ant-design/icons/EyeOutlined';

// project imports
import MainCard from 'components/MainCard';
import RichTextField from 'components/RichTextField';
import SignaturePad from 'components/SignaturePad';
import IEPSectionUploads from 'sections/supervisor/iep/IEPSectionUploads';
import ImportIepDialog from 'sections/supervisor/iep/ImportIepDialog';
import { useStudentRegistration } from 'contexts/StudentRegistrationContext';
import usePageAccess from 'hooks/usePageAccess';
import usePageIcon from 'hooks/usePageIcon';
import { saveClientIepData } from 'services/api/clients';
import { uploadIepDocument } from 'services/api/iepMaterials';
import { createClientIepVersion, updateIepVersion, createGoal, updateGoal, deleteGoal, listGoals } from 'services/api/iep';
import GoalBuilderModal from 'sections/shared/skill-repo/GoalBuilderModal';
import GoalDetailsModal from 'sections/shared/skill-repo/GoalDetailsModal';
import BehaviorEditorDialog from 'sections/shared/child-detail/BehaviorEditorDialog';
import { findDuplicateBehavior, otherBehaviorNames } from 'utils/behaviorNames';
import { skillDomains } from 'api/skillsRepository';
import {
  ASSESSMENT_TYPES,
  ASSESSMENT_TYPE_OPTIONS,
  LEGACY_ASSESSMENT_FIELDS,
  createBlankAssessment,
  createBlankRow,
  migrateLegacyAssessments,
  reshapeAssessmentForType
} from 'utils/iepAssessments';
import { maskChildName } from 'utils/maskChildName';
import { activityLines } from 'utils/activityList';
import { GOAL_SERVICES, serviceLabel } from 'utils/goalServices';

// ==============================|| IEP — INDIVIDUALIZED EDUCATION PROGRAM ||============================== //

// Goal domains are sourced from the Skills Repository (single source of
// truth). The id (e.g. 'DOMAIN-MAND') is stored on the goal; the name is
// shown to the user.
const GOAL_DOMAIN_OPTIONS = skillDomains.map((d) => ({ id: d.id, name: d.name }));

const UNASSIGNED_DOMAIN_ID = '__unassigned__';
// Synthetic group for goals no service has been chosen for yet. A sentinel
// rather than '' so it can be an ordering key like any real service code.
const UNASSIGNED_SERVICE_ID = '__no_service__';

// Map a legacy free-text domain (e.g. 'Mand', 'Tact', 'Listener Response')
// to a skillDomains id. Tries exact-name match, then case-insensitive prefix
// match against the domain name. Anything unrecognised falls back to
// UNASSIGNED so the goal still shows up and the user can pick a real domain.
//
// IMPORTANT: any 'DOMAIN-*' id and any bare Skill-Repository code ('READ',
// 'VPMTS', 'MI', …) passes through UNTOUCHED (bare codes get the 'DOMAIN-'
// prefix so they match the live domainOptions ids). The fuzzy name matching
// below is ONLY for genuine legacy free-text values. Previously repo codes
// fell through to the fuzzy matcher — which only knows the 11 hardcoded
// legacy domains — and got rewritten to '__unassigned__' (or a wrong match)
// and then SAVED back to the snapshot, so a goal shown under READ in the
// Goals & Targets tab appeared under a different domain in the IEP document.
const legacyDomainToId = (raw) => {
  if (!raw) return '';
  const trimmed = String(raw).trim();
  if (!trimmed || trimmed === UNASSIGNED_DOMAIN_ID) return trimmed;
  // Already a domain id (legacy 'DOMAIN-VP-MTS' or live-repo 'DOMAIN-READ') —
  // keep it verbatim; the live domainOptions resolve the label/grouping.
  if (/^DOMAIN-/i.test(trimmed)) return trimmed;
  // Bare Skill-Repository code (uppercase letters/digits/hyphens, no spaces)
  // as mirrored by the Goals & Targets tab — canonicalize to the id form.
  if (/^[A-Z0-9][A-Z0-9-]*$/.test(trimmed)) return `DOMAIN-${trimmed}`;
  const exact = skillDomains.find((d) => d.name.toLowerCase() === trimmed.toLowerCase());
  if (exact) return exact.id;
  const partial = skillDomains.find(
    (d) => trimmed.toLowerCase().includes(d.name.toLowerCase()) || d.name.toLowerCase().includes(trimmed.toLowerCase())
  );
  return partial ? partial.id : UNASSIGNED_DOMAIN_ID;
};

const IEP_GOAL_STATUS_OPTIONS = [
  { value: 'not_started', labelKey: 'iep.goalStatus.notStarted' },
  { value: 'in_progress', labelKey: 'iep.goalStatus.inProgress' },
  { value: 'on_hold', labelKey: 'iep.goalStatus.onHold' },
  { value: 'completed', labelKey: 'iep.goalStatus.completed' }
];

// Next free "G-00n" code — derived from the HIGHEST existing number, not the
// array length. Length-based numbering collides after a delete (delete G-002 of
// {G-001,G-002,G-003} → length 2 → next "G-003", a duplicate), and a duplicate
// goal_code makes the live-row create 409 and silently drop the goal ("goal not
// saved sometimes"). Mirrors GoalBuilderModal.nextCode.
const nextSuggestedGoalId = (existing) => {
  let max = 0;
  for (const g of existing || []) {
    const m = /(\d+)\s*$/.exec(g?.goalId || '');
    if (m) max = Math.max(max, parseInt(m[1], 10));
  }
  return `G-${String(max + 1).padStart(3, '0')}`;
};

// Legacy goals had `targetDate` + a subset of fields. Reshape them in place so
// the new form fields all render with sane defaults: legacy `targetDate`
// flows into `endDate`, legacy `description` becomes the new `name` (falling
// back to the existing `name` if a previous save already populated it), and
// free-text legacy domains map to a skillDomains id.
const upgradeGoal = (goal, idx) => ({
  goalId: goal?.goalId || `G-${String(idx + 1).padStart(3, '0')}`,
  name: goal?.name || goal?.description || '',
  status: goal?.status || '',
  startDate: goal?.startDate || '',
  endDate: goal?.endDate || goal?.targetDate || '',
  domain: legacyDomainToId(goal?.domain),
  baseline: goal?.baseline || '',
  // Which service delivers the goal (clinical.ref_service.code) and, for the
  // therapy services, the session activities that work it.
  serviceCode: goal?.serviceCode || '',
  activities: activityLines(goal?.activities),
  // The narrative under the statement, labelled "Goal description" in the goal
  // dialog. `description` is the legacy carrier of the STATEMENT itself and is
  // left alone.
  goalDescription: goal?.goalDescription || '',
  // Kept for backward-compat with existing saves; not surfaced in the new UI.
  description: goal?.description || '',
  notes: goal?.notes || '',
  targets: Array.isArray(goal?.targets) ? goal.targets : []
});

// p13 = Instructional Delivery, p14 = Assessment Summary, p15 = Recommendation.
// Old panel ids (p1/p2/p4/p6/p7/p8/p12) keep the same numbers so saved
// collapse-state continues to map. The legacy p9 (was "Summary") is replaced
// by p14, and was never persisted as collapse state in any user-visible way.
const ALL_PANELS = ['p1', 'p2', 'p4', 'p7', 'p13', 'p6', 'p8', 'p14', 'p15', 'p12'];

// PBSP / Behavior of Concern — Geetika's form maps to the schema below.
// Each behavior is its own card with a description block, function block,
// and a measurement-mode block (frequency / duration / intensity checkboxes
// + free-text notes — the user wanted to be able to indicate which
// dimension(s) are actually being tracked).
// The purpose/reinforcement checkbox catalogues now live in BehaviorEditorDialog
// (the shared add/edit modal). blankBehavior() is kept here because hydration and
// PDF-import (upgradeBehavior) still seed behaviors with the canonical shape.

const blankBehavior = () => ({
  behaviorName: '',
  // Description block
  operationalDefinition: '',
  measurement: {
    trackFrequency: false,
    trackDuration: false,
    trackIntensity: false,
    notes: ''
  },
  settings: '',
  motivatingOperations: '',
  antecedents: '',
  consequences: '',
  // Function block — Hypothesized purpose
  purpose: {
    emotionalReactivity: false,
    cognitiveDistortions: false,
    modeling: false,
    physiologicalPurpose: false,
    communicateNeeds: false,
    curriculumInstruction: false,
    other: '',
    description: ''
  },
  // Function block — Type of reinforcement maintaining the behavior
  reinforcement: {
    gainAttention: false,
    gainTangible: false,
    gainSensoryInput: false,
    fillHabitualNeed: false,
    escapeTask: false,
    socialAvoidance: false,
    releaseTension: false,
    escapeSensoryInput: false,
    other: '',
    description: ''
  }
});

// Legacy behaviors[] used a flat 4-col table: { behaviorName, frequency,
// duration, intensity }. Reshape into the new card schema; legacy free-text
// freq/dur/intensity values are concatenated into measurement.notes so no
// data is lost on first re-open.
const upgradeBehavior = (b) => {
  const base = blankBehavior();
  if (!b || typeof b !== 'object') return base;
  // If the row is already in the new shape, accept it as-is (deep-merged onto defaults so missing keys fill in).
  if (b.measurement && typeof b.measurement === 'object') {
    return {
      ...base,
      ...b,
      measurement: { ...base.measurement, ...b.measurement },
      purpose: { ...base.purpose, ...(b.purpose || {}) },
      reinforcement: { ...base.reinforcement, ...(b.reinforcement || {}) }
    };
  }
  const legacyNotes = [
    b.frequency ? `Frequency: ${b.frequency}` : null,
    b.duration ? `Duration: ${b.duration}` : null,
    b.intensity ? `Intensity: ${b.intensity}` : null
  ]
    .filter(Boolean)
    .join(' • ');
  return {
    ...base,
    behaviorName: b.behaviorName || '',
    measurement: {
      trackFrequency: Boolean(b.frequency),
      trackDuration: Boolean(b.duration),
      trackIntensity: Boolean(b.intensity),
      notes: legacyNotes
    }
  };
};

// Parent goals were previously `{ description }` only. They now mirror the
// IEP goal shape (minus the skill domain) — short name, dates, status, and
// a baseline. Legacy `description` is preserved on the shape for
// backward-compat with older saves.
const upgradeParentGoal = (goal, idx) => ({
  goalId: goal?.goalId || `PG-${String(idx + 1).padStart(3, '0')}`,
  name: goal?.name || goal?.description || '',
  status: goal?.status || '',
  startDate: goal?.startDate || '',
  endDate: goal?.endDate || goal?.targetDate || '',
  baseline: goal?.baseline || '',
  description: goal?.description || '',
  notes: goal?.notes || ''
});

const MAX_ASSESSMENT_ENTRIES = 2;

const initialFormData = {
  // Report Identification
  childName: '',
  childId: '',
  dateOfBirth: '',
  parentNames: '',
  address: '',
  services: '',
  assessmentStartDate: '',
  assessmentEndDate: '',
  reportDate: '',
  supervisor: '',
  startDate: '',
  endDate: '',
  iepVersion: '',

  // Background — single rich-text field. The six legacy fields below were
  // collapsed into `backgroundNotes`; they're kept on the form-data shape so
  // we can migrate them in on first hydration (see useEffect below). New
  // saves only write `backgroundNotes`.
  backgroundNotes: '',
  backgroundNarrative: '',
  behavioralChallenges: '',
  selfStimulationBehaviors: '',
  assessmentObservations: '',
  preferences: '',
  culturalNotes: '',

  // Assessment Reports — up to MAX_ASSESSMENT_ENTRIES entries, each with a
  // chosen type (VB-MAPP / ABLLS-R) and dynamic row tables per section.
  assessments: [],

  // Instructional Delivery — rich text describing teaching environment,
  // 1:1 ratio, reinforcement schedule, etc.
  instructionalDelivery: '',

  // PBSP / Behaviour Intervention Plan
  operationalDefinitions: '', // rich text — what each target behavior looks like operationally
  behaviors: [], // [{ behaviorName, frequency, duration, intensity }]
  pbspStrategies: [], // ABC Strategy table — [{ antecedent, training, consequence }]

  // Goals
  iepGoals: [],

  // Parent / Caregiver section (expanded)
  parentGoalsSummary: '', // rich text — summary of family priorities/context
  parentGoals: [],
  parentTrainingSchedule: '', // rich text — frequency/format of parent training
  transitionPlan: '', // rich text — when/how to transition out of intensive support

  // Assessment Summary (was "Summary") — rich text
  summary: '',
  // Recommendation — rich text — next steps and clinical recommendation
  recommendation: '',

  // Sign-off
  parentSignature: null,
  supervisorSignature: null
};

// Read-only display for a saved signature data URI.
function ReadOnlySignature({ label, dataUrl }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      {dataUrl ? (
        <Box
          component="img"
          src={dataUrl}
          alt={label}
          sx={{
            maxHeight: 90,
            maxWidth: '100%',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 0.5,
            bgcolor: 'background.paper'
          }}
        />
      ) : (
        <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
          —
        </Typography>
      )}
    </Box>
  );
}

// "Mark Not Applicable" pill that lives in the section header.
// Clicking toggles the section into an NA state; the parent decides what to
// render in the body (typically: an info Alert + a "reason" textarea).
function SectionNAToggle({ isNA, onToggle, theme }) {
  return (
    <Tooltip title={isNA ? 'This section is marked as Not Applicable. Click to re-enable.' : 'Mark this entire section as Not Applicable'}>
      <ToggleButtonGroup
        value={isNA ? 'na' : null}
        exclusive
        onChange={(e, val) => {
          // Don't let the click bubble up to the section header (which would toggle expand/collapse).
          e.stopPropagation();
          onToggle(val === 'na');
        }}
        onClick={(e) => e.stopPropagation()}
        size="small"
      >
        <ToggleButton
          value="na"
          sx={{
            px: 1.5,
            fontSize: '0.75rem',
            fontWeight: 600,
            '&.Mui-selected': {
              bgcolor: alpha(theme.palette.warning.main, 0.12),
              color: theme.palette.warning.dark,
              borderColor: theme.palette.warning.main,
              '&:hover': { bgcolor: alpha(theme.palette.warning.main, 0.2) }
            }
          }}
        >
          NA
        </ToggleButton>
      </ToggleButtonGroup>
    </Tooltip>
  );
}

// Section card with a clickable header that toggles a Collapse below.
// `headerExtra` renders between the title and the chevron — used for the NA toggle.
function SectionPanel({ title, subtitle, expanded, onToggle, theme, headerExtra, children }) {
  return (
    <MainCard sx={{ mb: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" onClick={onToggle} sx={{ cursor: 'pointer' }}>
        <Typography variant="h5" fontWeight={600} sx={{ flex: 1 }}>
          {title}
        </Typography>
        {headerExtra}
        <DownOutlined
          style={{
            fontSize: '0.75rem',
            color: theme.palette.text.secondary,
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s'
          }}
        />
      </Stack>
      <Collapse in={expanded}>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {subtitle}
          </Typography>
        )}
        <Divider sx={{ my: 1.5 }} />
        {children}
      </Collapse>
    </MainCard>
  );
}

export default function IEP({
  readOnly: readOnlyProp = false,
  returnPath: returnPathProp = null,
  // When 'version', saving writes to the clinical.iep_version table.
  // - If `editVersionId` is set, the save UPDATES that existing version
  //   in place (PUT /iep/versions/{id}) — used by the Edit flow.
  // - Otherwise the save CREATES a new version (POST /iep/clients/{cid}/versions)
  //   — used by the Add New IEP Version flow.
  // The legacy 'preferences' path (default) keeps writing to client.preferences.
  versioningMode = 'preferences',
  // Optional override for the client id when versioningMode='version' and
  // the form is mounted outside a StudentRegistration flow (e.g., the
  // /supervisor/child/:childId/iep/new route).
  clientIdOverride = null,
  // When set + versioningMode='version', save updates this version row
  // instead of creating a new one. Used by the Edit (in-place) flow.
  editVersionId = null
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();
  // Match the sidebar: use the icon configured in Features and Components for
  // this route, falling back to the static one until the catalogue loads.
  const HeaderIcon = usePageIcon(FileTextOutlined);
  // Full-Read on the IEP page (child_iep) — or the parent Child Profiles feature: the IEP
  // form opens read-only (fields disabled via the fieldset below), no save. Composes with prop.
  const { readOnly: isCodeReadOnly, canSeeCode } = usePageAccess();
  const readOnly = readOnlyProp || isCodeReadOnly('child_iep');

  const { saveFormData, markFormCompleted, syncFormToShared, getSharedFieldsForForm, getFormData, sharedData } = useStudentRegistration();

  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [initialized, setInitialized] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  // SAVE IS IN FLIGHT. Writing an IEP version takes a moment, and with nothing on
  // screen to say so a supervisor reasonably concludes the click did not land and
  // clicks again - which is how three clicks became three saved IEP versions for one
  // child. The ref is the actual guard and the state only drives the button: state
  // updates are not applied synchronously, so two clicks landing in the same tick
  // would both see `saving === false`, whereas the ref is set the instant the first
  // one is handled.
  const savingRef = useRef(false);
  const [saving, setSaving] = useState(false);

  // Unsaved-changes tracking. `dirtyRef` flips true on the first user edit after
  // hydration and drives both the in-app navigation blocker and the browser
  // beforeunload prompt (both read the ref at fire time, so no re-render is
  // needed). `skipBlockRef` lets an intentional leave (Cancel or a completed
  // Save) bypass the prompt.
  const dirtyRef = useRef(false);
  const skipBlockRef = useRef(false);
  const markDirty = () => {
    dirtyRef.current = true;
  };
  const clearDirty = () => {
    dirtyRef.current = false;
  };

  // Goal-domain options come from the Skills Repository (clinical.skill_domain).
  // Seeded with the static list as a fallback; replaced by the live domains on
  // mount. The id stays `DOMAIN-{CODE}` so it matches what goals already store.
  const [domainOptions, setDomainOptions] = useState(GOAL_DOMAIN_OPTIONS);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { listSkillDomains } = await import('services/api/skills');
        const domains = await listSkillDomains();
        if (cancelled || !Array.isArray(domains) || domains.length === 0) return;
        const opts = domains.filter((d) => d.code && d.name).map((d) => ({ id: `DOMAIN-${d.code}`, name: d.name }));
        // De-dupe by display name — some domains share a name (e.g. two
        // "Listener Responding" rows); keep the first (respects backend order).
        const seen = new Set();
        setDomainOptions(
          opts.filter((o) => {
            const key = o.name.toLowerCase();
            return seen.has(key) ? false : seen.add(key);
          })
        );
      } catch {
        /* keep the static fallback */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Section collapse/expand state. Editing an existing version opens with every
  // section COLLAPSED (the document is already filled — the author scans, then
  // expands the section they want); creating a new IEP opens fully expanded so
  // it can be filled top to bottom.
  const [expandedPanels, setExpandedPanels] = useState(() => (editVersionId ? new Set() : new Set(ALL_PANELS)));
  const togglePanel = (panel) => () =>
    setExpandedPanels((prev) => {
      const next = new Set(prev);
      if (next.has(panel)) next.delete(panel);
      else next.add(panel);
      return next;
    });
  const expandAll = () => setExpandedPanels(new Set(ALL_PANELS));
  const collapseAll = () => setExpandedPanels(new Set());

  /**
   * Which boxes this import left empty, so the form can SHOW the gaps.
   *
   * The receipt banner lists them, but a list above the form is not where someone
   * fills a field in - they read it, scroll down, and lose it. Marking the control
   * itself is what turns "three things are missing" into "this box needs you".
   *
   * Two reasons, worded differently because they mean different things: the report
   * was read and had nothing for this one, versus this one is never taken from a
   * document at all (the IEP's own window and version - see schema.ALWAYS_MANUAL).
   *
   * SELF-CLEARING. The mark is conditional on the field still being empty, so it
   * disappears as the reviewer types rather than needing to be dismissed. Rich text
   * arrives as HTML, so an "empty" editor is stripped of tags before the check -
   * Quill leaves "<p><br></p>" behind when you clear it.
   */
  const [importUnfilled, setImportUnfilled] = useState(null);

  const importGap = (name) => {
    const reason = importUnfilled?.get(name);
    if (!reason) return null;
    const raw = formData[name];
    const text = String(raw ?? '')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();
    return text ? null : reason;
  };

  // Three reasons a box came back empty, and they do NOT read the same. 'manual'
  // and 'not_in_report' both ask the supervisor for something; 'not_applicable'
  // asks for nothing - a speech or OT report has no behaviour support plan and no
  // parent-training programme, so telling someone to go and fill those in sends
  // them looking through the PDF for a section that was never written.
  const IMPORT_GAP_KEYS = {
    manual: 'iepImport.gap.manual',
    not_applicable: 'iepImport.gap.notApplicable',
    not_in_report: 'iepImport.gap.notInReport'
  };
  const importGapText = (reason) => intl.formatMessage({ id: IMPORT_GAP_KEYS[reason] || IMPORT_GAP_KEYS.not_in_report });

  // Not applicable is a statement, not a to-do, so it is set in the ordinary
  // secondary text rather than the amber that means "this box still needs you".
  const importGapColor = (reason) => (reason === 'not_applicable' ? 'text.secondary' : 'warning.main');

  /** Props to spread onto a TextField so an unfilled box reads as needing attention. */
  const importGapProps = (name) => {
    const reason = importGap(name);
    // A validation error outranks a gap: "required" is a blocker, "the report did
    // not have this" is information, and showing both on one field says neither.
    if (!reason || errors[name]) return {};
    if (reason === 'not_applicable') {
      // No highlight at all - the box is empty because it should be.
      return {
        helperText: importGapText(reason),
        FormHelperTextProps: { sx: { color: 'text.secondary' } }
      };
    }
    return {
      // Not `error`: nothing is wrong, something is missing. Red would tell a
      // supervisor they have made a mistake by importing.
      color: 'warning',
      focused: true,
      helperText: importGapText(reason),
      FormHelperTextProps: { sx: { color: 'warning.main' } }
    };
  };

  /** The same mark for a rich-text section, which has no helper text of its own. */
  const importGapNote = (name) => {
    const reason = importGap(name);
    if (!reason) return null;
    return (
      <Typography
        variant="caption"
        sx={{ display: 'block', mb: 0.75, color: importGapColor(reason), fontWeight: reason === 'not_applicable' ? 400 : 600 }}
      >
        {importGapText(reason)}
      </Typography>
    );
  };

  // ---- Import from an assessment report -----------------------------------
  // The dialog reads the PDF and hands back a PATCH in this form's own shape. All
  // this code does is merge it, which is deliberately the smaller half of the job:
  // see backend/app/services/iep_import.
  const [importOpen, setImportOpen] = useState(false);
  // What the last import filled in, so the form can say so and then get out of the
  // way. Dismissible, and cleared on save - it is a receipt, not a mode.
  const [importSummary, setImportSummary] = useState(null);

  /**
   * Fill the form from an imported assessment report.
   *
   * A PATCH, NEVER A REPLACEMENT, and the distinction is doing real work here. Lists
   * are APPENDED, not assigned: a supervisor who has already added two goals by hand
   * and then imports a report keeps both, and gets the report's goals after them.
   * Assigning would silently delete work that is not recoverable from the PDF.
   * Scalars overwrite, because the patch only carries fields the document actually
   * answered - there is nothing in it to overwrite a field the document was silent
   * about.
   *
   * Three things are minted CLIENT-SIDE rather than by the server, because all three
   * depend on what is already on the form and the server cannot see that:
   *   - goalId / parent goalId, which continue the existing numbering.
   *   - each assessment entry's `id`, which keys its uploaded grid images.
   *   - each assessment row's `rowId`, which is its React key.
   */
  const handleImportApply = async (patch, meta = {}) => {
    if (!patch || Object.keys(patch).length === 0) return;
    // An import writes into the form via setFormData (not setField), so flag the
    // unsaved-changes state explicitly.
    markDirty();

    const newId = () =>
      typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `i_${Date.now()}_${Math.random().toString(36).slice(2)}`;

    // Built before setFormData so the image uploads below can key off the same ids.
    const entries = (patch.assessments || []).map((entry) => ({
      ...createBlankAssessment(entry.type),
      ...entry,
      id: newId(),
      sections: Object.fromEntries(
        Object.entries(entry.sections || {}).map(([key, section]) => [
          key,
          { ...section, rows: (section.rows || []).map((row) => ({ ...row, rowId: row.rowId || newId() })) }
        ])
      )
    }));

    setFormData((prev) => {
      const next = { ...prev };
      Object.entries(patch).forEach(([key, value]) => {
        if (!Array.isArray(value)) {
          next[key] = value;
        }
      });

      // Goal ids continue the existing run rather than restarting at G-001, which
      // would collide with a goal already on the form and, after save, with a live
      // iep_goal row keyed on goal_code.
      const goalBase = (prev.iepGoals || []).length;
      next.iepGoals = [
        ...(prev.iepGoals || []),
        ...(patch.iepGoals || []).map((goal, i) => ({
          ...goal,
          goalId: `G-${String(goalBase + i + 1).padStart(3, '0')}`
        }))
      ];

      const parentBase = (prev.parentGoals || []).length;
      next.parentGoals = [
        ...(prev.parentGoals || []),
        ...(patch.parentGoals || []).map((goal, i) => ({
          ...goal,
          goalId: `PG-${String(parentBase + i + 1).padStart(3, '0')}`
        }))
      ];

      next.behaviors = [...(prev.behaviors || []), ...(patch.behaviors || []).map(upgradeBehavior)];
      next.pbspStrategies = [...(prev.pbspStrategies || []), ...(patch.pbspStrategies || [])];
      // Assessments are the one list that is CAPPED, at MAX_ASSESSMENT_ENTRIES - the
      // form renders no more than that, so an entry beyond it would be stored and
      // never shown.
      next.assessments = [...(prev.assessments || []), ...entries].slice(0, MAX_ASSESSMENT_ENTRIES);
      return next;
    });

    // Every panel opens: values landing inside a collapsed section would change the
    // form invisibly, and the whole point is that a person now reads it as a form.
    setExpandedPanels(new Set(ALL_PANELS));
    // The gaps the server named, keyed for the per-field marks above.
    setImportUnfilled(new Map((meta.unfilled || []).map((f) => [f.name, f.reason])));
    setImportSummary({
      fileName: meta.fileName || '',
      lines: meta.summary || [],
      needsAttention: meta.needsAttention || [],
      images: 0
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // The grid images are not part of the snapshot - the form keeps them in file
    // storage, per assessment section - so they are uploaded the same way a drag-and
    // drop would, now that their entry has an id. Needs a child id to file under; in
    // a first-time registration there is not one yet, and the reviewer is told so
    // rather than the images vanishing silently.
    const uploaded = await uploadImportedGrids(meta.images || [], entries, formData.childId);
    setImportSummary((prev) => (prev ? { ...prev, ...uploaded } : prev));
    // The upload panels for these entries mounted with setFormData above and listed an
    // EMPTY directory, because the pictures are only written on the line before this
    // one. Nothing else re-lists them, so without this the grids the import just filed
    // stayed invisible until some unrelated upload happened to refresh the panel.
    if (uploaded.images > 0) setUploadsReloadToken((n) => n + 1);
  };

  /**
   * Put each extracted grid image under the assessment section it was read from.
   *
   * Best-effort by design: a failed upload costs the picture, never the import. The
   * values are already in the form by the time this runs.
   */
  /* Bumped after an import files its grid images, to make the per-section upload panels
   * re-list. They cannot notice on their own: the files appear in storage behind them,
   * with no state of theirs changing. */
  const [uploadsReloadToken, setUploadsReloadToken] = useState(0);

  const uploadImportedGrids = async (images, entries, childId) => {
    if (!images.length) return { images: 0 };
    if (!childId) {
      return {
        images: 0,
        imageNote: intl.formatMessage({ id: 'iepImport.imagesNeedChild' }, { count: images.length })
      };
    }
    let done = 0;
    for (const image of images) {
      const entry = entries.find((e) => e.sections && image.section && e.sections[image.section]);
      if (!entry || !image.data_url) continue;
      try {
        const response = await fetch(image.data_url);
        const blob = await response.blob();
        const name = `${image.section}-grid-p${image.page}.png`;
        await uploadIepDocument({
          childId,
          section: `assessment-${entry.id}-${image.section}`,
          file: new File([blob], name, { type: image.mime || 'image/png' })
        });
        done += 1;
      } catch (err) {
        console.warn('[IEP] grid image upload failed:', err?.message);
      }
    }
    return { images: done };
  };

  // Refs for the three signature pads (parent / supervisor / approver).
  const parentSigRef = useRef(null);
  const supervisorSigRef = useRef(null);

  const returnPath = returnPathProp || location.state?.returnPath || '/supervisor/add-student';

  useEffect(() => {
    if (initialized) return;
    const shared = getSharedFieldsForForm('iep') || {};
    const cachedRaw = getFormData?.('iep') || null;
    // Don't let empty-string keys in a previously-saved IEP blob clobber values
    // the Enquiry / Intake supplied via shared data. The identity fields
    // (childName / childId / dateOfBirth / gender) are read-only here and OWNED
    // by the earlier forms — a blank left in the IEP cache from a prior save
    // must never win over the live enquiry value, which is exactly what used to
    // blank out Child Name while the header still showed it. Same guard the
    // Enquiry form already applies to its own cache.
    const cached = {};
    if (cachedRaw) {
      Object.entries(cachedRaw).forEach(([k, v]) => {
        if (v !== '' && v !== null && v !== undefined) cached[k] = v;
      });
    }
    setFormData((prev) => {
      const merged = { ...prev, ...shared, ...cached };
      // First-time load of a legacy IEP: convert the flat milestone / barrier
      // score fields into a single VB-MAPP entry under `assessments`.
      const migrated = migrateLegacyAssessments(merged, cachedRaw?.sectionNA);
      if (migrated) {
        merged.assessments = migrated;
        for (const key of LEGACY_ASSESSMENT_FIELDS) delete merged[key];
      } else if (!Array.isArray(merged.assessments)) {
        merged.assessments = [];
      }
      // Upgrade goals saved before the goalId / status / start / notes / targets
      // fields were introduced. Any legacy `targetDate` flows into `endDate`.
      if (Array.isArray(merged.iepGoals)) {
        merged.iepGoals = merged.iepGoals.map(upgradeGoal);
      }
      if (Array.isArray(merged.parentGoals)) {
        merged.parentGoals = merged.parentGoals.map(upgradeParentGoal);
      }
      if (!Array.isArray(merged.behaviors)) {
        merged.behaviors = [];
      } else {
        merged.behaviors = merged.behaviors.map(upgradeBehavior);
      }
      // Background fields were six separate text areas before; now collapsed
      // into a single rich-text `backgroundNotes`. If the new field is empty
      // but legacy fields have content, stitch them together with subheadings
      // so the supervisor can keep editing without losing prior work.
      if (!merged.backgroundNotes || !String(merged.backgroundNotes).trim()) {
        const legacySections = [
          { label: 'Background narrative', value: merged.backgroundNarrative },
          { label: 'Behavioral challenges', value: merged.behavioralChallenges },
          { label: 'Self-stimulatory behaviors', value: merged.selfStimulationBehaviors },
          { label: 'Assessment period observations', value: merged.assessmentObservations },
          { label: 'Preferences observed', value: merged.preferences },
          { label: 'Cultural / religious notes', value: merged.culturalNotes }
        ].filter((s) => s.value && String(s.value).trim());
        if (legacySections.length > 0) {
          merged.backgroundNotes = legacySections.map((s) => `<h4>${s.label}</h4>${s.value}`).join('');
        }
      }
      return merged;
    });
    setInitialized(true);
  }, [initialized, getSharedFieldsForForm, getFormData]);

  const setField = (key, value) => {
    // Only user edits (after hydration) count as unsaved changes.
    if (initialized) markDirty();
    setFormData((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const addPbspStrategy = () => setField('pbspStrategies', [...formData.pbspStrategies, { antecedent: '', training: '', consequence: '' }]);
  const removePbspStrategy = (idx) =>
    setField(
      'pbspStrategies',
      formData.pbspStrategies.filter((_, i) => i !== idx)
    );
  const updatePbspStrategy = (idx, key, val) =>
    setField(
      'pbspStrategies',
      formData.pbspStrategies.map((s, i) => (i === idx ? { ...s, [key]: val } : s))
    );

  // Add a goal built from the Skill Repository (GoalBuilderModal). GOALS ONLY
  // here — targets are attached later in the child's Goals & Targets tab.
  const [repoBuilderOpen, setRepoBuilderOpen] = useState(false);
  // Free-form custom goal add/edit (shared GoalDetailsModal). null = closed;
  // { editIdx: null } = add; { editIdx: n } = edit the goal at that index.
  const [customGoalModal, setCustomGoalModal] = useState(null);
  // Index of the goal pending delete-confirmation (null = no dialog open).
  const [goalDeleteIdx, setGoalDeleteIdx] = useState(null);
  // Goal code to flash with a green border right after an add/edit scrolls to
  // it. Auto-clears ~4s later (effect below).
  const [highlightGoalCode, setHighlightGoalCode] = useState(null);

  // Open the Goals section and scroll the goal at `idx` into view after an
  // add/edit, so the author immediately sees what they created without hunting,
  // then flash it green for a few seconds.
  const scrollToIepGoal = (idx, goalId) => {
    setExpandedPanels((prevPanels) => (prevPanels.has('p7') ? prevPanels : new Set(prevPanels).add('p7')));
    // Two rAFs — wait for React commit + layout before scrolling.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-goal-idx="${idx}"]`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (goalId) setHighlightGoalCode(goalId);
      })
    );
  };

  // Clear the green highlight ~4s after it lands.
  useEffect(() => {
    if (!highlightGoalCode) return undefined;
    const t = setTimeout(() => setHighlightGoalCode(null), 4000);
    return () => clearTimeout(t);
  }, [highlightGoalCode]);

  const handleRepoGoalAdd = (g) => {
    const newIdx = formData.iepGoals.length; // appended at the end
    setField('iepGoals', [
      ...formData.iepGoals,
      {
        goalId: g.goalId,
        name: g.statement || g.name,
        description: g.statement || g.name,
        status: g.status === 'mastered' ? 'completed' : g.status || 'in_progress',
        startDate: g.startDate || '',
        endDate: g.endDate || '',
        // Canonical domain id ('DOMAIN-' + bare repo code) so grouping and the
        // Skill domain dropdown resolve immediately, without waiting for the
        // next load's legacyDomainToId pass.
        domain: g.domainCode ? `DOMAIN-${String(g.domainCode).replace(/^DOMAIN-/i, '')}` : '',
        baseline: g.baseline || '',
        // Repository goals carry no service or activities of their own; the
        // author sets them afterwards via the pencil.
        serviceCode: '',
        activities: [],
        goalDescription: '',
        notes: '',
        targetCriteria: g.targetCriteria || '',
        targets: []
      }
    ]);
    setRepoBuilderOpen(false);
    scrollToIepGoal(newIdx, g.goalId);
  };

  // After a version save, mirror the form's goals into live clinical.iep_goal
  // rows for that version — the child's Goals & Targets tab reads those rows,
  // so goals added on the IEP form show up there too. Idempotent by goal_code.
  // For goal codes that already have a live row, a differing skill domain is
  // pushed to the row too, so re-assigning a goal's domain on this form shows
  // up in the Goals & Targets tab instead of silently diverging from it.
  const syncGoalsToLiveRows = async (versionRes, goals) => {
    const iepId = versionRes?.iep_id;
    const versionId = versionRes?.id;
    if (!iepId || !versionId) return;
    // Live rows store the bare Skill-Repository code ('READ'); the form stores
    // the id form ('DOMAIN-READ'). Compare and write bare.
    const bareDomain = (v) => {
      const c = String(v || '')
        .replace(/^DOMAIN-/i, '')
        .trim();
      return c === UNASSIGNED_DOMAIN_ID ? '' : c;
    };
    // Goal codes whose live-row create/update failed. Returned to the caller so
    // the save no longer reports a clean success while goals silently went
    // missing from the Goals & Targets tab ("goal not saved sometimes"). The
    // tab also self-heals on load, but the author should still be told.
    const failed = [];
    const existing = new Map(); // goal_code -> live row
    try {
      const res = await listGoals(iepId, { iep_version_id: versionId, page_size: 500 });
      for (const r of res?.items || res || []) existing.set(r.goal_code, r);
    } catch {
      /* listing failed — creation below still guards via backend 409 */
    }
    for (const g of goals || []) {
      const text = (g?.name || g?.description || '').trim();
      if (!g?.goalId || !text) continue;
      const row = existing.get(g.goalId);
      const activities = activityLines(g.activities);
      try {
        if (row) {
          // Only send what actually differs — an unconditional PUT per goal on
          // every version save would rewrite (and re-audit) rows nobody touched.
          const patch = {};
          const wanted = bareDomain(g.domain);
          if (wanted && wanted !== bareDomain(row.skill_domain_code)) patch.skill_domain_code = wanted;
          if (text !== (row.description || '')) patch.description = text;
          if ((g.goalDescription || '') !== (row.goal_description || '')) patch.goal_description = g.goalDescription || null;
          if ((g.serviceCode || '') !== (row.service_code || '')) patch.service_code = g.serviceCode || null;
          if (JSON.stringify(activities) !== JSON.stringify(activityLines(row.activities))) patch.activities = activities;
          if (Object.keys(patch).length > 0) await updateGoal(iepId, row.id, patch);
          continue;
        }
        await createGoal(iepId, {
          iep_id: iepId,
          iep_version_id: versionId,
          goal_code: g.goalId,
          skill_domain_code: bareDomain(g.domain) || null,
          service_code: g.serviceCode || null,
          description: text,
          goal_description: g.goalDescription || null,
          activities,
          baseline: g.baseline || null,
          target_criteria: g.targetCriteria || null,
          start_date: g.startDate || null,
          target_date: g.endDate || null,
          status_code: 'active'
        });
      } catch (err) {
        console.warn(`[IEP] goal sync to live rows failed for ${g.goalId}:`, err?.message);
        failed.push(g.goalId);
      }
    }
    // Reconcile deletions: any live row whose goal was removed from the form on
    // this version must be soft-deleted, otherwise the Goals & Targets tab —
    // which reads the iep_goal table, not the snapshot — keeps showing goals
    // (and their targets) the user already deleted here. Backend soft-delete
    // cascades to the goal's targets, and the tab fetches targets per-goal, so
    // removed goals drop out of the tab entirely.
    const keptCodes = new Set((goals || []).map((g) => g?.goalId).filter(Boolean));
    for (const [code, row] of existing) {
      if (keptCodes.has(code)) continue;
      try {
        await deleteGoal(iepId, row.id);
      } catch (err) {
        console.warn(`[IEP] goal delete sync failed for ${code}:`, err?.message);
      }
    }
    return { failed };
  };
  const removeIepGoal = (idx) =>
    setField(
      'iepGoals',
      formData.iepGoals.filter((_, i) => i !== idx)
    );
  // Add OR edit a free-form (custom) goal via the shared GoalDetailsModal.
  // customGoalModal.editIdx === null means add (append); otherwise patch that
  // goal in place. After the write the Goals section is opened and the goal is
  // scrolled into view so the author immediately sees what they added/edited.
  const handleCustomGoalSave = (data) => {
    const editIdx = customGoalModal?.editIdx;
    const list = [...formData.iepGoals];
    const prev = editIdx != null ? list[editIdx] : null;
    const entry = {
      ...(prev || {}),
      goalId: data.goalId,
      name: data.name,
      description: data.name,
      goalDescription: data.goalDescription || '',
      serviceCode: data.serviceCode || '',
      activities: data.activities || [],
      status: data.status || prev?.status || 'in_progress',
      startDate: data.startDate || '',
      endDate: data.endDate || '',
      domain: data.domainId || '',
      baseline: data.baseline || '',
      notes: prev?.notes || '',
      targetCriteria: prev?.targetCriteria || '',
      targets: prev?.targets || []
    };
    const scrollIdx = editIdx != null ? editIdx : list.length;
    if (editIdx != null) list[editIdx] = entry;
    else list.push(entry);
    setField('iepGoals', list);
    setCustomGoalModal(null);
    scrollToIepGoal(scrollIdx, data.goalId);
  };

  // ---- Assessment Reports handlers ----------------------------------------
  // The `assessments` array holds up to MAX_ASSESSMENT_ENTRIES entries. Each
  // entry has a type (vbmapp / ablls), an optional NA flag + reason, and a
  // per-section row table that the user builds explicitly via "+ Add row".
  const addAssessment = () => {
    if (formData.assessments.length >= MAX_ASSESSMENT_ENTRIES) return;
    setField('assessments', [...formData.assessments, createBlankAssessment('')]);
  };
  const removeAssessment = (idx) =>
    setField(
      'assessments',
      formData.assessments.filter((_, i) => i !== idx)
    );
  const updateAssessment = (idx, patch) =>
    setField(
      'assessments',
      formData.assessments.map((a, i) => (i === idx ? { ...a, ...patch } : a))
    );
  const updateAssessmentType = (idx, typeKey) =>
    setField(
      'assessments',
      formData.assessments.map((a, i) => (i === idx ? reshapeAssessmentForType(a, typeKey) : a))
    );
  const updateAssessmentSection = (idx, sectionKey, patch) =>
    setField(
      'assessments',
      formData.assessments.map((a, i) =>
        i === idx
          ? {
              ...a,
              sections: {
                ...a.sections,
                [sectionKey]: { ...(a.sections?.[sectionKey] || { overall: '', rows: [] }), ...patch }
              }
            }
          : a
      )
    );
  const addAssessmentRow = (idx, sectionKey) => {
    const section = formData.assessments[idx]?.sections?.[sectionKey] || { overall: '', rows: [] };
    updateAssessmentSection(idx, sectionKey, { rows: [...section.rows, createBlankRow()] });
  };
  const removeAssessmentRow = (idx, sectionKey, rowId) => {
    const section = formData.assessments[idx]?.sections?.[sectionKey] || { overall: '', rows: [] };
    updateAssessmentSection(idx, sectionKey, { rows: section.rows.filter((r) => r.rowId !== rowId) });
  };
  const updateAssessmentRow = (idx, sectionKey, rowId, patch) => {
    const section = formData.assessments[idx]?.sections?.[sectionKey] || { overall: '', rows: [] };
    updateAssessmentSection(idx, sectionKey, {
      rows: section.rows.map((r) => (r.rowId === rowId ? { ...r, ...patch } : r))
    });
  };

  const addParentGoal = () =>
    setField('parentGoals', [
      ...formData.parentGoals,
      {
        goalId: `PG-${String(formData.parentGoals.length + 1).padStart(3, '0')}`,
        name: '',
        status: 'in_progress',
        startDate: '',
        endDate: '',
        baseline: '',
        description: '',
        notes: ''
      }
    ]);
  const removeParentGoal = (idx) =>
    setField(
      'parentGoals',
      formData.parentGoals.filter((_, i) => i !== idx)
    );
  const updateParentGoal = (idx, key, val) =>
    setField(
      'parentGoals',
      formData.parentGoals.map((g, i) => (i === idx ? { ...g, [key]: val } : g))
    );

  // Behaviors (PBSP — Behavior of Concern) handlers.
  // Authoring happens in the shared BehaviorEditorDialog (modal), mirroring how
  // goals are authored via GoalDetailsModal. `behaviorModal` holds { editIdx }:
  // null editIdx = add. `behaviorViewIdx` opens the modal read-only (view), and
  // `behaviorDeleteIdx` drives the delete confirmation.
  const [behaviorModal, setBehaviorModal] = useState(null);
  const [behaviorViewIdx, setBehaviorViewIdx] = useState(null);
  const [behaviorDeleteIdx, setBehaviorDeleteIdx] = useState(null);

  // Add OR edit a behavior. A newly added behavior is PREPENDED (shows at the
  // top of the list) and flagged `_isNew` so it stays highlighted until the
  // form is saved. The transient `_isNew` flag is stripped from the payload in
  // performSave, so it never reaches the persisted snapshot.
  const handleBehaviorSave = (behavior) => {
    const editIdx = behaviorModal?.editIdx;
    if (findDuplicateBehavior(behavior.behaviorName, formData.behaviors, editIdx ?? null)) return;
    const list = [...(formData.behaviors || [])];
    let scrollIdx;
    if (editIdx != null) {
      // BehaviorEditorDialog echoes back any existing `_isNew` flag, so an
      // in-place edit preserves the highlight state of an unsaved behavior.
      list[editIdx] = behavior;
      scrollIdx = editIdx;
    } else {
      list.unshift({ ...behavior, _isNew: true });
      scrollIdx = 0;
    }
    setField('behaviors', list);
    setBehaviorModal(null);
    // Make sure the PBSP panel is open, then scroll the affected card into view.
    setExpandedPanels((prev) => (prev.has('p6') ? prev : new Set(prev).add('p6')));
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-behavior-idx="${scrollIdx}"]`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      })
    );
  };

  const removeBehaviorAt = (idx) =>
    setField(
      'behaviors',
      (formData.behaviors || []).filter((_, i) => i !== idx)
    );

  const validate = () => {
    const next = {};
    if (!formData.startDate) next.startDate = intl.formatMessage({ id: 'validation.required' });
    if (!formData.endDate) next.endDate = intl.formatMessage({ id: 'validation.required' });
    if (formData.startDate && formData.endDate && formData.endDate < formData.startDate) {
      next.endDate = intl.formatMessage({ id: 'iep.endBeforeStart' });
    }
    if (!formData.iepVersion?.trim()) next.iepVersion = intl.formatMessage({ id: 'validation.required' });
    setErrors(next);
    if (Object.keys(next).length > 0) {
      // Make sure the section containing the bad field is open so the user sees it.
      setExpandedPanels((prev) => new Set([...prev, 'p1']));
      return false;
    }
    return true;
  };

  const performSave = async () => {
    if (!validate()) {
      setSnack({ open: true, message: intl.formatMessage({ id: 'iep.fixErrors' }), severity: 'error' });
      return;
    }
    // Strip any legacy flat assessment fields that may have lingered through
    // hydration before persisting; the canonical shape is `assessments[]`.
    const payload = { ...formData };
    for (const key of LEGACY_ASSESSMENT_FIELDS) delete payload[key];
    // Drop the transient `_isNew` highlight flag so it never reaches the snapshot.
    if (Array.isArray(payload.behaviors)) {
      payload.behaviors = payload.behaviors.map(({ _isNew, ...rest }) => rest);
    }
    setImportSummary(null);
    setImportUnfilled(null);
    saveFormData('iep', payload);
    syncFormToShared('iep', payload);
    markFormCompleted('iep');

    // versioningMode='version' — save writes to clinical.iep_version.
    // If `editVersionId` is set, we UPDATE that version in place. Otherwise
    // we CREATE a new version. The IEP & Documents tab uses the former for
    // the Edit button (acts on the current row) and the latter for the
    // Add New IEP Version button.
    if (versioningMode === 'version') {
      const versionClientId = clientIdOverride || sharedData?.clientId;
      if (!editVersionId && !versionClientId) {
        setSnack({ open: true, message: intl.formatMessage({ id: 'iep.missingClientId' }), severity: 'error' });
        return;
      }
      try {
        let versionRes;
        if (editVersionId) {
          versionRes = await updateIepVersion(editVersionId, {
            snapshot_data: payload,
            change_summary: payload?.changeSummary || null
          });
        } else {
          versionRes = await createClientIepVersion(versionClientId, {
            snapshot_data: payload,
            change_summary: payload?.changeSummary || null
          });
        }
        // IEP form ↔ Goals & Targets consistency: goals authored here become
        // live iep_goal rows for this version (goals only; targets are added
        // in the Goals & Targets tab).
        const sync = await syncGoalsToLiveRows(versionRes, payload.iepGoals);
        // The version itself saved. If some goals failed to become live rows,
        // warn rather than reporting a clean success — the Goals & Targets tab
        // self-heals those on load, so we still navigate away.
        if (sync?.failed?.length) {
          setSnack({
            open: true,
            message: intl.formatMessage({ id: 'iep.savedGoalSyncWarning' }, { count: sync.failed.length }),
            severity: 'warning'
          });
          setTimeout(() => navigate(returnPath), 1200);
          return true;
        }
      } catch (err) {
        console.error('[IEP] Version save failed:', err);
        setSnack({
          open: true,
          message: err?.response?.data?.detail || intl.formatMessage({ id: 'iep.fixErrors' }),
          severity: 'error'
        });
        return;
      }
      setSnack({ open: true, message: intl.formatMessage({ id: 'iep.saved' }), severity: 'success' });
      setTimeout(() => navigate(returnPath), 600);
      return true;
    }

    // Default (legacy) path: the child profile is created up-front at the Enquiry
    // step now, so by the time the IEP is saved a client already exists (either
    // created this session by the Enquiry, or resumed from a draft). Persist the IEP
    // straight to that client so it survives a refresh and shows on the Children
    // page. If somehow there's no client yet, the IEP blob is still held in the
    // registration context and sent along when the supervisor clicks "Add Child
    // Profile".
    const clientId = sharedData?.clientId;
    if (clientId) {
      try {
        await saveClientIepData(clientId, payload);
      } catch (err) {
        console.error('[IEP] Server save failed:', err);
        setSnack({
          open: true,
          message: err?.response?.data?.detail || intl.formatMessage({ id: 'iep.fixErrors' }),
          severity: 'error'
        });
        return;
      }
    }

    setSnack({ open: true, message: intl.formatMessage({ id: 'iep.saved' }), severity: 'success' });
    setTimeout(() => navigate(returnPath), 600);
    return true;
  };

  // The only thing the button calls. Refuses a second run while the first is still
  // going, so a repeated click is ignored rather than writing another IEP version.
  //
  // RELEASED ON EVERY OUTCOME BUT SUCCESS. performSave returns true only where it has
  // actually written and scheduled the navigation away; it returns early, without
  // throwing, when validation fails or the server refuses. Holding the flag in those
  // cases would leave the supervisor looking at a permanently disabled Save with a
  // form they have just been asked to correct. On a real save the flag stays set:
  // the page navigates 600ms later, and re-enabling the button inside that window
  // would reopen the very hole this closes.
  const handleSave = async () => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    let saved = false;
    try {
      saved = (await performSave()) === true;
    } finally {
      if (!saved) {
        savingRef.current = false;
        setSaving(false);
      } else {
        // A successful save navigates away (after a short delay) — let that and
        // any subsequent navigation through without the unsaved-changes prompt.
        skipBlockRef.current = true;
        clearDirty();
      }
    }
  };

  // Cancel is an intentional leave, so bypass the unsaved-changes prompt.
  const handleCancel = () => {
    skipBlockRef.current = true;
    navigate(returnPath);
  };

  // Block in-app navigation (sidebar, back button, links) while there are
  // unsaved edits. The callback reads refs so it always sees the latest state
  // without needing to re-subscribe on every keystroke.
  const blocker = useBlocker(() => !skipBlockRef.current && dirtyRef.current && !readOnly);

  // Warn on browser tab close / refresh with unsaved edits.
  useEffect(() => {
    if (readOnly) return undefined;
    const handler = (e) => {
      if (!dirtyRef.current) return undefined;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [readOnly]);

  // Child Name is the identity that must come from the Enquiry. Child ID is
  // minted later (at intake) and is legitimately absent while only the enquiry
  // exists, so it does NOT drive the "missing" banner — warning on it here read
  // as "the pre-fill is broken" even when everything was filled correctly.
  const childNameMissing = !formData.childName?.trim();

  const sectionLabels = {
    expand: intl.formatMessage({ id: 'common.expandAll' }),
    collapse: intl.formatMessage({ id: 'common.collapseAll' })
  };

  return (
    <Box>
      {/* Page Header */}
      <MainCard
        sx={{
          mb: 3,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white'
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Button onClick={handleCancel} startIcon={<LeftOutlined />} sx={{ color: 'white', minWidth: 0 }}>
            {intl.formatMessage({ id: 'common.back' })}
          </Button>
          <Box
            sx={{
              width: 60,
              height: 60,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 2,
              bgcolor: alpha('#ffffff', 0.2)
            }}
          >
            <HeaderIcon style={{ fontSize: '2rem' }} />
          </Box>
          <Box>
            <Typography
              variant="h2"
              sx={(t) => ({
                fontWeight: 700,
                [t.breakpoints.down('sm')]: {
                  fontSize: '1.25rem',
                  lineHeight: 1.25
                }
              })}
            >
              {intl.formatMessage({ id: 'registration.iep' })}
            </Typography>
            <Typography
              variant="body1"
              sx={(t) => ({
                opacity: 0.9,
                mt: 0.5,
                [t.breakpoints.down('sm')]: {
                  fontSize: '0.75rem'
                }
              })}
            >
              {sharedData?.childName?.trim()
                ? intl.formatMessage({ id: 'iep.subtitleFor' }, { name: maskChildName(sharedData.childName.trim()) })
                : intl.formatMessage({ id: 'registration.iepDesc' })}
            </Typography>
          </Box>
        </Stack>
      </MainCard>

      {readOnly && (
        <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
          {intl.formatMessage({ id: 'iep.readOnlyBanner' })}
        </Alert>
      )}

      {!readOnly && childNameMissing && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
          {intl.formatMessage({ id: 'iep.prefillMissing' })}
        </Alert>
      )}

      {/* What the last import filled in. A receipt, not a mode - dismissible, and
          gone on save. It names what landed and what the document could not answer,
          because an import that quietly fills eight sections is worse than one that
          says which eight. */}
      {importSummary && (
        <Alert
          severity="success"
          variant="outlined"
          sx={{ mb: 2 }}
          action={
            <IconButton
              size="small"
              onClick={() => {
                setImportSummary(null);
                setImportUnfilled(null);
              }}
              aria-label={intl.formatMessage({ id: 'common.close' })}
            >
              <CloseOutlined style={{ fontSize: '0.75rem' }} />
            </IconButton>
          }
        >
          <Typography variant="subtitle2">
            {intl.formatMessage({ id: 'iepImport.banner.title' }, { file: importSummary.fileName })}
          </Typography>
          {importSummary.lines.length > 0 && (
            <Typography variant="body2" sx={{ mt: 0.25 }}>
              {importSummary.lines.join(' · ')}
            </Typography>
          )}
          {importSummary.images > 0 && (
            <Typography variant="body2" sx={{ mt: 0.25 }}>
              {intl.formatMessage({ id: 'iepImport.banner.images' }, { count: importSummary.images })}
            </Typography>
          )}
          {importSummary.imageNote && (
            <Typography variant="body2" color="warning.main" sx={{ mt: 0.25 }}>
              {importSummary.imageNote}
            </Typography>
          )}
          {importSummary.needsAttention.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>
                {intl.formatMessage({ id: 'iepImport.banner.check' }, { count: importSummary.needsAttention.length })}
              </Typography>
              <Typography variant="body2" component="ul" sx={{ m: 0, pl: 2.5 }}>
                {importSummary.needsAttention.map((item) => (
                  <li key={`${item.key}-${item.label}`}>
                    <strong>{item.label}</strong> — {item.reason}
                  </li>
                ))}
              </Typography>
            </>
          )}
        </Alert>
      )}

      {/* Import / Expand / Collapse */}
      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mb: 1.5 }}>
        {/* Hidden when the capability is switched off in Features and Components or
            un-ticked for this role in Role Definitions - see the `iep_pdf_import`
            page code. Read-only viewers never get it either: importing is a write. */}
        {!readOnly && canSeeCode('iep_pdf_import') && (
          <Button size="small" variant="contained" startIcon={<FilePdfOutlined />} onClick={() => setImportOpen(true)}>
            {intl.formatMessage({ id: 'iepImport.button' })}
          </Button>
        )}
        <Button
          size="small"
          variant="outlined"
          startIcon={<DownOutlined style={{ fontSize: '0.7rem' }} />}
          disabled={expandedPanels.size === ALL_PANELS.length}
          onClick={expandAll}
        >
          {sectionLabels.expand}
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<UpOutlined style={{ fontSize: '0.7rem' }} />}
          disabled={expandedPanels.size === 0}
          onClick={collapseAll}
        >
          {sectionLabels.collapse}
        </Button>
      </Stack>

      {/* Disable every native input inside this fieldset when read-only */}
      {/* `disabled` covers native inputs; MUI Select/DatePicker render a clickable div that
          the fieldset does NOT disable, so also cut pointer events on MUI input roots when
          read-only — that closes the dropdown/calendar. Buttons and text stay usable. */}
      <Box
        component="fieldset"
        disabled={readOnly}
        sx={{ border: 0, p: 0, m: 0, minWidth: 0, ...(readOnly && { '& .MuiInputBase-root': { pointerEvents: 'none' } }) }}
      >
        {/* p1: Report Identification */}
        <SectionPanel
          panelId="p1"
          title={intl.formatMessage({ id: 'iep.section.identification' })}
          subtitle={intl.formatMessage({ id: 'iep.section.identificationDesc' })}
          expanded={expandedPanels.has('p1')}
          onToggle={togglePanel('p1')}
          theme={theme}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                label={intl.formatMessage({ id: 'iep.childName' })}
                value={formData.childName || ''}
                disabled
                helperText={intl.formatMessage({ id: 'iep.prefilledHelp' })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                label={intl.formatMessage({ id: 'iep.childId' })}
                value={formData.childId || ''}
                disabled
                helperText={intl.formatMessage({ id: 'iep.prefilledHelp' })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                type="date"
                label={intl.formatMessage({ id: 'iep.dateOfBirth' })}
                InputLabelProps={{ shrink: true }}
                value={formData.dateOfBirth || ''}
                disabled
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={intl.formatMessage({ id: 'iep.parentNames' })}
                value={formData.parentNames}
                onChange={(e) => setField('parentNames', e.target.value)}
                {...importGapProps('parentNames')}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={intl.formatMessage({ id: 'iep.supervisor' })}
                value={formData.supervisor}
                onChange={(e) => setField('supervisor', e.target.value)}
                {...importGapProps('supervisor')}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={intl.formatMessage({ id: 'iep.address' })}
                value={formData.address}
                onChange={(e) => setField('address', e.target.value)}
                {...importGapProps('address')}
                multiline
                minRows={2}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label={intl.formatMessage({ id: 'iep.services' })}
                placeholder="e.g. Applied Behavior Analysis (ABA) 2 hours per day, 5 days a week"
                value={formData.services}
                onChange={(e) => setField('services', e.target.value)}
                {...importGapProps('services')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                type="date"
                label={intl.formatMessage({ id: 'iep.assessmentStartDate' })}
                InputLabelProps={{ shrink: true }}
                value={formData.assessmentStartDate}
                onChange={(e) => setField('assessmentStartDate', e.target.value)}
                {...importGapProps('assessmentStartDate')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                type="date"
                label={intl.formatMessage({ id: 'iep.assessmentEndDate' })}
                InputLabelProps={{ shrink: true }}
                value={formData.assessmentEndDate}
                onChange={(e) => setField('assessmentEndDate', e.target.value)}
                {...importGapProps('assessmentEndDate')}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                type="date"
                label={intl.formatMessage({ id: 'iep.reportDate' })}
                InputLabelProps={{ shrink: true }}
                value={formData.reportDate}
                onChange={(e) => setField('reportDate', e.target.value)}
                {...importGapProps('reportDate')}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Divider sx={{ my: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {intl.formatMessage({ id: 'iep.iepPeriodHeading' })}
                </Typography>
              </Divider>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                type="date"
                required
                label={intl.formatMessage({ id: 'iep.startDate' })}
                InputLabelProps={{ shrink: true }}
                value={formData.startDate}
                onChange={(e) => setField('startDate', e.target.value)}
                error={Boolean(errors.startDate)}
                helperText={errors.startDate || ' '}
                {...importGapProps('startDate')}
                inputProps={{ max: formData.endDate || undefined }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                fullWidth
                type="date"
                required
                label={intl.formatMessage({ id: 'iep.endDate' })}
                InputLabelProps={{ shrink: true }}
                value={formData.endDate}
                onChange={(e) => setField('endDate', e.target.value)}
                error={Boolean(errors.endDate)}
                helperText={errors.endDate || ' '}
                {...importGapProps('endDate')}
                inputProps={{ min: formData.startDate || undefined }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 4 }}>
              <TextField
                fullWidth
                required
                label={intl.formatMessage({ id: 'iep.iepVersion' })}
                placeholder="e.g. v1.0, Annual 2026, Initial"
                value={formData.iepVersion}
                onChange={(e) => setField('iepVersion', e.target.value)}
                error={Boolean(errors.iepVersion)}
                helperText={errors.iepVersion || ' '}
                {...importGapProps('iepVersion')}
              />
            </Grid>
          </Grid>
        </SectionPanel>

        {/* p2: Background */}
        <SectionPanel
          panelId="p2"
          title={intl.formatMessage({ id: 'iep.section.background' })}
          subtitle={intl.formatMessage({ id: 'iep.section.backgroundDesc' })}
          expanded={expandedPanels.has('p2')}
          onToggle={togglePanel('p2')}
          theme={theme}
        >
          {importGapNote('backgroundNotes')}
          <RichTextField
            label={intl.formatMessage({ id: 'iep.backgroundNotes' })}
            value={formData.backgroundNotes}
            onChange={(v) => setField('backgroundNotes', v)}
            readOnly={readOnly}
            minHeight={360}
          />
        </SectionPanel>

        {/* p4: Assessment Reports — up to two assessment entries, each with a
            type dropdown (VB-MAPP / ABLLS-R) and dynamic row tables per section. */}
        <SectionPanel
          panelId="p4"
          title={intl.formatMessage({ id: 'iep.section.assessments' })}
          subtitle={intl.formatMessage({ id: 'iep.section.assessmentsDesc' })}
          expanded={expandedPanels.has('p4')}
          onToggle={togglePanel('p4')}
          theme={theme}
        >
          <Stack spacing={3}>
            {formData.assessments.length === 0 && (
              <Typography variant="body2" color="text.disabled" textAlign="center" sx={{ py: 2 }}>
                {intl.formatMessage({ id: 'iep.noAssessmentsYet' })}
              </Typography>
            )}

            {formData.assessments.map((entry, entryIdx) => {
              const typeDef = ASSESSMENT_TYPES[entry.type] || null;
              return (
                <Paper key={entry.id} variant="outlined" sx={{ p: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
                      {intl.formatMessage({ id: 'iep.assessmentEntryNumber' }, { n: entryIdx + 1 })}
                    </Typography>
                    <SectionNAToggle isNA={!!entry.isNA} onToggle={(isNA) => updateAssessment(entryIdx, { isNA })} theme={theme} />
                    {!readOnly && (
                      <Tooltip title={intl.formatMessage({ id: 'iep.removeAssessment' })}>
                        <IconButton size="small" color="error" onClick={() => removeAssessment(entryIdx)}>
                          <DeleteOutlined />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                      <TextField
                        select
                        fullWidth
                        label={intl.formatMessage({ id: 'iep.assessmentType' })}
                        value={entry.type || ''}
                        onChange={(e) => updateAssessmentType(entryIdx, e.target.value)}
                      >
                        <MenuItem value="">—</MenuItem>
                        {ASSESSMENT_TYPE_OPTIONS.map((opt) => (
                          <MenuItem key={opt.key} value={opt.key}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>

                  {entry.isNA ? (
                    <Stack spacing={2} sx={{ mt: 2 }}>
                      <Alert severity="info">{intl.formatMessage({ id: 'iep.naSection' })}</Alert>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label={intl.formatMessage({ id: 'iep.naReason' })}
                        placeholder={intl.formatMessage({ id: 'iep.naReasonPlaceholder' })}
                        value={entry.naReason || ''}
                        onChange={(e) => updateAssessment(entryIdx, { naReason: e.target.value })}
                      />
                    </Stack>
                  ) : typeDef ? (
                    <Stack spacing={2.5} sx={{ mt: 2 }}>
                      {typeDef.sections.map((sectionDef) => {
                        const section = entry.sections?.[sectionDef.key] || { overall: '', rows: [] };
                        const rows = section.rows || [];
                        return (
                          <Box
                            key={sectionDef.key}
                            sx={{
                              // Subtle accent so each section reads as its own
                              // grouped block (Milestones / Barriers) without
                              // dominating the layout.
                              pl: 1.75,
                              borderLeft: `3px solid ${theme.palette.primary.main}`,
                              py: 1.5
                            }}
                          >
                            <Stack
                              direction={{ xs: 'column', sm: 'row' }}
                              alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                              justifyContent="space-between"
                              spacing={2}
                              sx={{
                                mb: 2,
                                pb: 1.5,
                                borderBottom: `1px solid ${theme.palette.divider}`
                              }}
                            >
                              <Typography variant="h5" fontWeight={700} sx={{ pt: 0.5, color: theme.palette.text.primary }}>
                                {intl.formatMessage({ id: sectionDef.labelKey })}
                              </Typography>
                              <TextField
                                type="number"
                                size="small"
                                label={intl.formatMessage({ id: 'iep.overallScore' })}
                                value={section.overall}
                                onChange={(e) => updateAssessmentSection(entryIdx, sectionDef.key, { overall: e.target.value })}
                                inputProps={{ min: 0, max: sectionDef.maxOverall, step: 0.5 }}
                                helperText={intl.formatMessage({ id: 'iep.outOfPoints' }, { max: sectionDef.maxOverall })}
                                sx={{ width: { xs: '100%', sm: 200 }, flexShrink: 0 }}
                              />
                            </Stack>

                            {/* Per-section image upload — sits between the Overall score
                                header and the individual skill rows. */}
                            <Box sx={{ mb: 2 }}>
                              <IEPSectionUploads
                                childId={formData.childId}
                                section={`assessment-${entry.id}-${sectionDef.key}`}
                                imageOnly
                                readOnly={readOnly}
                                reloadToken={uploadsReloadToken}
                              />
                            </Box>

                            {rows.length === 0 ? (
                              <Typography variant="body2" color="text.disabled" sx={{ py: 1 }}>
                                {intl.formatMessage({ id: 'iep.noRowsYet' })}
                              </Typography>
                            ) : (
                              <Stack spacing={1.25}>
                                {rows.map((row) => (
                                  <Grid container key={row.rowId} spacing={1.5} alignItems="flex-start">
                                    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                      <TextField
                                        select
                                        size="small"
                                        fullWidth
                                        label={intl.formatMessage({ id: 'iep.skill' })}
                                        value={row.domainKey}
                                        onChange={(e) =>
                                          updateAssessmentRow(entryIdx, sectionDef.key, row.rowId, {
                                            domainKey: e.target.value
                                          })
                                        }
                                      >
                                        <MenuItem value="">—</MenuItem>
                                        {sectionDef.domains.map((d) => (
                                          <MenuItem key={d.key} value={d.key}>
                                            {d.label}
                                          </MenuItem>
                                        ))}
                                      </TextField>
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 2, md: 2 }}>
                                      <TextField
                                        type="number"
                                        size="small"
                                        fullWidth
                                        label={intl.formatMessage({ id: 'iep.score' })}
                                        value={row.score}
                                        onChange={(e) =>
                                          updateAssessmentRow(entryIdx, sectionDef.key, row.rowId, {
                                            score: e.target.value
                                          })
                                        }
                                        inputProps={{ min: 0, max: sectionDef.maxScore, step: 0.5 }}
                                      />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 4, md: 5 }}>
                                      <TextField
                                        size="small"
                                        fullWidth
                                        label={intl.formatMessage({ id: 'iep.observations' })}
                                        multiline
                                        minRows={1}
                                        maxRows={4}
                                        value={row.notes}
                                        onChange={(e) =>
                                          updateAssessmentRow(entryIdx, sectionDef.key, row.rowId, {
                                            notes: e.target.value
                                          })
                                        }
                                      />
                                    </Grid>
                                    <Grid
                                      size={{ xs: 12, sm: 12, md: 1 }}
                                      sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-end', md: 'center' } }}
                                    >
                                      {!readOnly && (
                                        <Tooltip title={intl.formatMessage({ id: 'iep.removeRow' })}>
                                          <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => removeAssessmentRow(entryIdx, sectionDef.key, row.rowId)}
                                          >
                                            <DeleteOutlined />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </Grid>
                                  </Grid>
                                ))}
                              </Stack>
                            )}

                            {!readOnly && (
                              <Button
                                size="small"
                                startIcon={<PlusOutlined />}
                                onClick={() => addAssessmentRow(entryIdx, sectionDef.key)}
                                sx={{ mt: 1 }}
                              >
                                {intl.formatMessage({ id: 'iep.addRow' })}
                              </Button>
                            )}
                          </Box>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
                      {intl.formatMessage({ id: 'iep.pickAssessmentType' })}
                    </Alert>
                  )}
                </Paper>
              );
            })}

            {!readOnly && formData.assessments.length < MAX_ASSESSMENT_ENTRIES && (
              <Button startIcon={<PlusOutlined />} variant="outlined" onClick={addAssessment} sx={{ alignSelf: 'flex-start' }}>
                {intl.formatMessage({ id: 'iep.addAssessment' })}
              </Button>
            )}
          </Stack>
        </SectionPanel>

        {/* p7: IEP Goals — rendered here (immediately after the Assessments
            section) so supervisors flow from observation → goal-setting in
            one pass. Panel id stays 'p7' so saved collapse state and any
            external references keep working after the visual reorder. */}
        <SectionPanel
          panelId="p7"
          title={intl.formatMessage({ id: 'iep.section.goals' })}
          subtitle={intl.formatMessage({ id: 'iep.section.goalsDesc' })}
          expanded={expandedPanels.has('p7')}
          onToggle={togglePanel('p7')}
          theme={theme}
        >
          {(() => {
            // Two levels of grouping: SERVICE, then skill domain inside it. A
            // supervisor works one service at a time — the speech goals are a
            // different conversation from the ABA ones — so the service is the
            // outer folder and each service keeps its own domain folders.
            //
            // Groups with zero goals are intentionally NOT rendered. Goals with
            // no domain set fall into a synthetic "Unassigned" domain at the top
            // of their service, and goals with no service into an "Unassigned
            // service" group at the end, so nothing is ever hidden and both stay
            // findable as the work queue for tagging them.
            const byService = new Map();
            for (let i = 0; i < formData.iepGoals.length; i++) {
              const g = formData.iepGoals[i];
              const svc = g.serviceCode || UNASSIGNED_SERVICE_ID;
              const did = g.domain || UNASSIGNED_DOMAIN_ID;
              if (!byService.has(svc)) byService.set(svc, new Map());
              const domains = byService.get(svc);
              if (!domains.has(did)) domains.set(did, []);
              domains.get(did).push({ goal: g, idx: i });
            }
            const orderDomains = (domainsWithGoals) => [
              ...(domainsWithGoals.has(UNASSIGNED_DOMAIN_ID) ? [UNASSIGNED_DOMAIN_ID] : []),
              // Skill-repo domains that have goals, in list order…
              ...domainOptions.filter((d) => domainsWithGoals.has(d.id)).map((d) => d.id),
              // …then any goal domain not in the options so no goal is ever hidden.
              ...[...domainsWithGoals.keys()].filter((id) => id !== UNASSIGNED_DOMAIN_ID && !domainOptions.some((d) => d.id === id))
            ];
            const orderedServiceIds = [
              ...GOAL_SERVICES.filter((sv) => byService.has(sv.code)).map((sv) => sv.code),
              // A code that is no longer in the service list still has to render.
              ...[...byService.keys()].filter((code) => code !== UNASSIGNED_SERVICE_ID && !GOAL_SERVICES.some((sv) => sv.code === code)),
              ...(byService.has(UNASSIGNED_SERVICE_ID) ? [UNASSIGNED_SERVICE_ID] : [])
            ];
            const iepStart = formData.startDate || '';
            const iepEnd = formData.endDate || '';
            const iepWindowReady = Boolean(iepStart && iepEnd);
            return (
              <Stack spacing={2}>
                {!iepWindowReady && (
                  <Alert severity="info" variant="outlined">
                    {intl.formatMessage({ id: 'iep.goalsNeedIepDates' })}
                  </Alert>
                )}

                {!readOnly && (
                  <Stack direction="row" justifyContent="flex-end" spacing={1}>
                    <Button
                      startIcon={<PlusOutlined />}
                      onClick={() => setCustomGoalModal({ editIdx: null })}
                      variant="contained"
                      size="small"
                    >
                      {intl.formatMessage({ id: 'iep.addGoal' })}
                    </Button>
                    <Button startIcon={<PlusOutlined />} onClick={() => setRepoBuilderOpen(true)} variant="outlined" size="small">
                      {intl.formatMessage({ id: 'iep.addFromSkillRepo' })}
                    </Button>
                  </Stack>
                )}

                {formData.iepGoals.length === 0 && (
                  <Typography variant="body2" color="text.disabled" textAlign="center" sx={{ py: 2 }}>
                    {intl.formatMessage({ id: 'iep.noGoalsYet' })}
                  </Typography>
                )}

                {orderedServiceIds.map((serviceId) => {
                  const domainsWithGoals = byService.get(serviceId) || new Map();
                  const orderedDomainIds = orderDomains(domainsWithGoals);
                  const serviceGoalCount = [...domainsWithGoals.values()].reduce((n, list) => n + list.length, 0);
                  const unassignedService = serviceId === UNASSIGNED_SERVICE_ID;
                  return (
                    <Accordion
                      key={serviceId}
                      defaultExpanded
                      disableGutters
                      sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 1,
                        '&:before': { display: 'none' },
                        bgcolor: 'background.paper'
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<DownOutlined style={{ fontSize: '0.75rem' }} />}
                        sx={{
                          bgcolor: alpha(unassignedService ? theme.palette.warning.main : theme.palette.info.main, 0.08),
                          borderBottom: `1px solid ${theme.palette.divider}`,
                          minHeight: 52
                        }}
                      >
                        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flex: 1 }}>
                          <Typography variant="subtitle1" fontWeight={700}>
                            {unassignedService ? intl.formatMessage({ id: 'iep.goalServiceUnassigned' }) : serviceLabel(serviceId)}
                          </Typography>
                          <Chip
                            size="small"
                            label={intl.formatMessage({ id: 'iep.goalCountChip' }, { count: serviceGoalCount })}
                            color={unassignedService ? 'warning' : 'info'}
                            variant="outlined"
                          />
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 2 }}>
                        <Stack spacing={2}>
                          {orderedDomainIds.map((domainId) => {
                            const matched = domainOptions.find((d) => d.id === domainId);
                            const label =
                              domainId === UNASSIGNED_DOMAIN_ID
                                ? intl.formatMessage({ id: 'iep.goalDomainUnassigned' })
                                : matched?.name || String(domainId).replace(/^DOMAIN-/, '');
                            const groupGoals = domainsWithGoals.get(domainId) || [];
                            return (
                              <Accordion
                                key={domainId}
                                defaultExpanded
                                disableGutters
                                sx={{
                                  border: `1px solid ${theme.palette.divider}`,
                                  borderRadius: 1,
                                  '&:before': { display: 'none' },
                                  bgcolor: 'background.paper'
                                }}
                              >
                                <AccordionSummary
                                  expandIcon={<DownOutlined style={{ fontSize: '0.75rem' }} />}
                                  sx={{
                                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                    minHeight: 48
                                  }}
                                >
                                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flex: 1 }}>
                                    <FolderOpenOutlined
                                      style={{
                                        fontSize: '1.1rem',
                                        color: domainId === UNASSIGNED_DOMAIN_ID ? theme.palette.warning.main : theme.palette.primary.main
                                      }}
                                    />
                                    <Typography variant="subtitle1" fontWeight={600}>
                                      {label}
                                    </Typography>
                                    <Chip
                                      size="small"
                                      label={intl.formatMessage({ id: 'iep.goalCountChip' }, { count: groupGoals.length })}
                                      color={domainId === UNASSIGNED_DOMAIN_ID ? 'warning' : 'primary'}
                                      variant="outlined"
                                    />
                                  </Stack>
                                </AccordionSummary>
                                <AccordionDetails sx={{ p: 2 }}>
                                  <Stack spacing={2}>
                                    {groupGoals.map(({ goal, idx }) => {
                                      // Read-only summary card — authoring happens in the shared
                                      // GoalDetailsModal (pencil). The goal's domain is already
                                      // conveyed by the folder it sits in, so it isn't repeated here.
                                      const statusOpt = IEP_GOAL_STATUS_OPTIONS.find((s) => s.value === goal.status);
                                      return (
                                        <Paper
                                          key={idx}
                                          data-goal-idx={idx}
                                          variant="outlined"
                                          sx={{
                                            p: 2,
                                            transition: (t) => t.transitions.create(['border-color', 'box-shadow']),
                                            ...(highlightGoalCode === goal.goalId && {
                                              borderColor: 'success.main',
                                              boxShadow: (t) => `0 0 0 2px ${alpha(t.palette.success.main, 0.45)}`
                                            })
                                          }}
                                        >
                                          <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
                                            <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
                                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                                <Typography variant="subtitle2" color="primary.main" fontWeight={700}>
                                                  {goal.goalId || '—'}
                                                </Typography>
                                                {goal.status && (
                                                  <Chip
                                                    size="small"
                                                    label={
                                                      statusOpt
                                                        ? intl.formatMessage({ id: statusOpt.labelKey })
                                                        : String(goal.status).replace(/_/g, ' ')
                                                    }
                                                    color={goal.status === 'completed' ? 'success' : 'default'}
                                                    variant={goal.status === 'completed' ? 'filled' : 'outlined'}
                                                  />
                                                )}
                                              </Stack>
                                              <Typography
                                                variant="body2"
                                                sx={{ wordBreak: 'break-word', color: goal.name ? 'text.primary' : 'text.disabled' }}
                                              >
                                                {goal.name || intl.formatMessage({ id: 'iep.goalName' })}
                                              </Typography>
                                              {goal.goalDescription && (
                                                <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                                  {goal.goalDescription}
                                                </Typography>
                                              )}
                                              {activityLines(goal.activities).length > 0 && (
                                                <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
                                                  {activityLines(goal.activities).map((act, i) => (
                                                    <Box component="li" key={i}>
                                                      <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                                        {act}
                                                      </Typography>
                                                    </Box>
                                                  ))}
                                                </Box>
                                              )}
                                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                {goal.serviceCode && (
                                                  <Chip
                                                    size="small"
                                                    color="info"
                                                    variant="outlined"
                                                    label={serviceLabel(goal.serviceCode)}
                                                  />
                                                )}
                                                {goal.startDate && (
                                                  <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    label={`${intl.formatMessage({ id: 'iep.goalStartDate' })}: ${goal.startDate}`}
                                                  />
                                                )}
                                                {goal.endDate && (
                                                  <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    label={`${intl.formatMessage({ id: 'iep.goalEndDate' })}: ${goal.endDate}`}
                                                  />
                                                )}
                                                {goal.baseline && (
                                                  <Tooltip title={goal.baseline} placement="top">
                                                    <Chip
                                                      size="small"
                                                      variant="outlined"
                                                      label={intl.formatMessage({ id: 'iep.goalBaseline' })}
                                                    />
                                                  </Tooltip>
                                                )}
                                              </Stack>
                                            </Stack>
                                            {!readOnly && (
                                              <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                                                <Tooltip title={intl.formatMessage({ id: 'common.edit' })}>
                                                  <IconButton size="small" onClick={() => setCustomGoalModal({ editIdx: idx })}>
                                                    <EditOutlined />
                                                  </IconButton>
                                                </Tooltip>
                                                <Tooltip title={intl.formatMessage({ id: 'iep.removeRow' })}>
                                                  <IconButton size="small" color="error" onClick={() => setGoalDeleteIdx(idx)}>
                                                    <DeleteOutlined />
                                                  </IconButton>
                                                </Tooltip>
                                              </Stack>
                                            )}
                                          </Stack>
                                        </Paper>
                                      );
                                    })}
                                  </Stack>
                                </AccordionDetails>
                              </Accordion>
                            );
                          })}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Stack>
            );
          })()}
        </SectionPanel>

        {/* p13: Instructional Delivery — rich-text describing how teaching
            sessions are run (ratio, environment, reinforcement schedule). */}
        <SectionPanel
          panelId="p13"
          title={intl.formatMessage({ id: 'iep.section.instructionalDelivery' })}
          subtitle={intl.formatMessage({ id: 'iep.section.instructionalDeliveryDesc' })}
          expanded={expandedPanels.has('p13')}
          onToggle={togglePanel('p13')}
          theme={theme}
        >
          {importGapNote('instructionalDelivery')}
          <RichTextField
            label={intl.formatMessage({ id: 'iep.instructionalDelivery' })}
            value={formData.instructionalDelivery}
            onChange={(v) => setField('instructionalDelivery', v)}
            readOnly={readOnly}
            minHeight={180}
          />
        </SectionPanel>

        {/* p6: PBSP — Operational Definitions (rich text), Behavior table,
            and the existing ABC Strategy table. Panel id stays 'p6'. */}
        <SectionPanel
          panelId="p6"
          title={intl.formatMessage({ id: 'iep.section.pbsp' })}
          subtitle={intl.formatMessage({ id: 'iep.section.pbspDesc' })}
          expanded={expandedPanels.has('p6')}
          onToggle={togglePanel('p6')}
          theme={theme}
        >
          <Stack spacing={3}>
            {/* Sub-section: Operational Definitions (rich text) */}
            <Box>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                {intl.formatMessage({ id: 'iep.pbsp.operationalDefinitions' })}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {intl.formatMessage({ id: 'iep.pbsp.operationalDefinitionsDesc' })}
              </Typography>
              {importGapNote('operationalDefinitions')}
              <RichTextField
                value={formData.operationalDefinitions}
                onChange={(v) => setField('operationalDefinitions', v)}
                readOnly={readOnly}
                minHeight={140}
              />
            </Box>

            {/* Sub-section: Behaviors of Concern — full Geetika schema. One
                card per behavior with description block, measurement-mode
                checkboxes, function (hypothesized purpose) checkboxes, and
                reinforcement-type checkboxes. */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
                  {intl.formatMessage({ id: 'iep.pbsp.behaviorsTitle' })}
                </Typography>
                {!readOnly && (
                  <Button size="small" variant="contained" startIcon={<PlusOutlined />} onClick={() => setBehaviorModal({ editIdx: null })}>
                    {intl.formatMessage({ id: 'iep.pbsp.addBehavior' })}
                  </Button>
                )}
              </Stack>

              {formData.behaviors.length === 0 ? (
                <Typography variant="body2" color="text.disabled" textAlign="center" sx={{ py: 3 }}>
                  {intl.formatMessage({ id: 'iep.pbsp.noBehaviors' })}
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {formData.behaviors.map((b, idx) => {
                    // Compact summary card — full authoring happens in the modal.
                    const dims = [];
                    if (b.measurement?.trackFrequency) dims.push(intl.formatMessage({ id: 'iep.pbsp.behavior.frequency' }));
                    if (b.measurement?.trackDuration) dims.push(intl.formatMessage({ id: 'iep.pbsp.behavior.duration' }));
                    if (b.measurement?.trackIntensity) dims.push(intl.formatMessage({ id: 'iep.pbsp.behavior.intensity' }));
                    return (
                      <Paper
                        key={idx}
                        data-behavior-idx={idx}
                        variant="outlined"
                        sx={{
                          p: 2,
                          transition: 'background-color 0.3s, border-color 0.3s',
                          // A newly added, not-yet-saved behavior stays highlighted.
                          ...(b._isNew && {
                            borderColor: theme.palette.success.main,
                            bgcolor: alpha(theme.palette.success.main, 0.08)
                          })
                        }}
                      >
                        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="flex-start">
                          <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                              <Typography variant="subtitle2" color="primary.main" fontWeight={700}>
                                {intl.formatMessage({ id: 'iep.pbsp.behaviorOfConcern' }, { n: idx + 1 })}
                              </Typography>
                              {b._isNew && (
                                <Chip size="small" color="success" label={intl.formatMessage({ id: 'iep.pbsp.behaviorNewChip' })} />
                              )}
                              {dims.map((label) => (
                                <Chip key={label} size="small" variant="outlined" label={label} />
                              ))}
                            </Stack>
                            <Typography
                              variant="body2"
                              sx={{ wordBreak: 'break-word', color: b.behaviorName ? 'text.primary' : 'text.disabled' }}
                            >
                              {b.behaviorName || intl.formatMessage({ id: 'iep.pbsp.behavior.name' })}
                            </Typography>
                            {b.operationalDefinition && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  wordBreak: 'break-word'
                                }}
                              >
                                {b.operationalDefinition}
                              </Typography>
                            )}
                          </Stack>
                          <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                            {readOnly ? (
                              <Tooltip title={intl.formatMessage({ id: 'common.view' })}>
                                <IconButton size="small" onClick={() => setBehaviorViewIdx(idx)}>
                                  <EyeOutlined />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <>
                                <Tooltip title={intl.formatMessage({ id: 'common.edit' })}>
                                  <IconButton size="small" onClick={() => setBehaviorModal({ editIdx: idx })}>
                                    <EditOutlined />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={intl.formatMessage({ id: 'iep.removeRow' })}>
                                  <IconButton size="small" color="error" onClick={() => setBehaviorDeleteIdx(idx)}>
                                    <DeleteOutlined />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                          </Stack>
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              )}
            </Box>

            {/* Sub-section: ABC Strategy table (kept as-is) */}
            <Box>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                {intl.formatMessage({ id: 'iep.pbsp.abcStrategyTitle' })}
              </Typography>
              <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                <Grid
                  container
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    px: 2,
                    py: 1
                  }}
                >
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {intl.formatMessage({ id: 'iep.pbspAntecedentStrategies' })}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {intl.formatMessage({ id: 'iep.pbspTrainingSupport' })}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {intl.formatMessage({ id: 'iep.pbspConsequenceStrategies' })}
                    </Typography>
                  </Grid>
                </Grid>
                {formData.pbspStrategies.length === 0 ? (
                  <Typography variant="body2" color="text.disabled" textAlign="center" sx={{ py: 3 }}>
                    {intl.formatMessage({ id: 'iep.pbspNoStrategies' })}
                  </Typography>
                ) : (
                  formData.pbspStrategies.map((row, idx) => {
                    // All three columns in a row share the same minRows so
                    // the cells align visually as a true table row. We
                    // estimate the rendered line count of each cell from
                    // hard newlines + ~45-char soft wraps and take the max.
                    const estimateLines = (text, perLine = 45) => {
                      if (!text) return 0;
                      return text.split('\n').reduce((n, line) => n + Math.max(1, Math.ceil(line.length / perLine)), 0);
                    };
                    const rowMinRows = Math.max(
                      3,
                      estimateLines(row.antecedent),
                      estimateLines(row.training),
                      estimateLines(row.consequence)
                    );
                    return (
                      <Grid
                        key={idx}
                        container
                        spacing={2}
                        sx={{
                          px: 2,
                          py: 1.5,
                          borderBottom: idx < formData.pbspStrategies.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                          alignItems: 'flex-start'
                        }}
                      >
                        <Grid size={{ xs: 12, md: 4 }}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={rowMinRows}
                            placeholder={intl.formatMessage({ id: 'iep.pbspAntecedentStrategies' })}
                            value={row.antecedent}
                            onChange={(e) => updatePbspStrategy(idx, 'antecedent', e.target.value)}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                          <TextField
                            fullWidth
                            multiline
                            minRows={rowMinRows}
                            placeholder={intl.formatMessage({ id: 'iep.pbspTrainingSupport' })}
                            value={row.training}
                            onChange={(e) => updatePbspStrategy(idx, 'training', e.target.value)}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 4 }}>
                          <Stack direction="row" spacing={1} alignItems="flex-start">
                            <TextField
                              fullWidth
                              multiline
                              minRows={rowMinRows}
                              placeholder={intl.formatMessage({ id: 'iep.pbspConsequenceStrategies' })}
                              value={row.consequence}
                              onChange={(e) => updatePbspStrategy(idx, 'consequence', e.target.value)}
                            />
                            {!readOnly && (
                              <Tooltip title={intl.formatMessage({ id: 'iep.removeRow' })}>
                                <IconButton size="small" onClick={() => removePbspStrategy(idx)} color="error">
                                  <DeleteOutlined />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </Grid>
                      </Grid>
                    );
                  })
                )}
              </Paper>
              {!readOnly && (
                <Button startIcon={<PlusOutlined />} onClick={addPbspStrategy} variant="outlined" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                  {intl.formatMessage({ id: 'iep.addPbspStrategy' })}
                </Button>
              )}
            </Box>
          </Stack>
        </SectionPanel>

        {/* p8: Parent Goals */}
        <SectionPanel
          panelId="p8"
          title={intl.formatMessage({ id: 'iep.section.parentGoals' })}
          subtitle={intl.formatMessage({ id: 'iep.section.parentGoalsDesc' })}
          expanded={expandedPanels.has('p8')}
          onToggle={togglePanel('p8')}
          theme={theme}
        >
          {(() => {
            const iepStart = formData.startDate || '';
            const iepEnd = formData.endDate || '';
            const iepWindowReady = Boolean(iepStart && iepEnd);
            return (
              <Stack spacing={3}>
                {/* Summary Info — rich text */}
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                    {intl.formatMessage({ id: 'iep.parent.summaryTitle' })}
                  </Typography>
                  {importGapNote('parentGoalsSummary')}
                  <RichTextField
                    value={formData.parentGoalsSummary}
                    onChange={(v) => setField('parentGoalsSummary', v)}
                    readOnly={readOnly}
                    minHeight={140}
                  />
                </Box>

                {/* Goals — same attributes as IEP goals minus skill domain */}
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
                      {intl.formatMessage({ id: 'iep.parent.goalsTitle' })}
                    </Typography>
                    {!readOnly && (
                      <Button size="small" startIcon={<PlusOutlined />} onClick={addParentGoal}>
                        {intl.formatMessage({ id: 'iep.addParentGoal' })}
                      </Button>
                    )}
                  </Stack>
                  {formData.parentGoals.length === 0 ? (
                    <Typography variant="body2" color="text.disabled" textAlign="center" sx={{ py: 2 }}>
                      {intl.formatMessage({ id: 'iep.noParentGoalsYet' })}
                    </Typography>
                  ) : (
                    <Stack spacing={2}>
                      {formData.parentGoals.map((goal, idx) => (
                        <Paper key={idx} variant="outlined" sx={{ p: 2 }}>
                          <Grid container spacing={1.5} alignItems="flex-start">
                            <Grid size={{ xs: 12, md: 11 }}>
                              <TextField
                                fullWidth
                                size="small"
                                label={intl.formatMessage({ id: 'iep.goalName' })}
                                value={goal.name || ''}
                                onChange={(e) => updateParentGoal(idx, 'name', e.target.value)}
                              />
                            </Grid>
                            <Grid size={{ xs: 12, md: 1 }} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                              {!readOnly && (
                                <Tooltip title={intl.formatMessage({ id: 'iep.removeRow' })}>
                                  <IconButton size="small" color="error" onClick={() => removeParentGoal(idx)}>
                                    <DeleteOutlined />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Grid>

                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                              <TextField
                                fullWidth
                                size="small"
                                label={intl.formatMessage({ id: 'iep.goalIdShort' })}
                                value={goal.goalId || ''}
                                InputProps={{ readOnly: true }}
                                helperText={intl.formatMessage({ id: 'iep.goalIdAutoGenerated' })}
                              />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                              <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label={intl.formatMessage({ id: 'iep.goalStartDate' })}
                                InputLabelProps={{ shrink: true }}
                                value={goal.startDate || ''}
                                onChange={(e) => updateParentGoal(idx, 'startDate', e.target.value)}
                                inputProps={{
                                  min: iepStart || undefined,
                                  max: goal.endDate || iepEnd || undefined
                                }}
                                helperText={
                                  iepWindowReady
                                    ? intl.formatMessage({ id: 'iep.goalDateBoundsHint' }, { start: iepStart, end: iepEnd })
                                    : ' '
                                }
                              />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                              <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label={intl.formatMessage({ id: 'iep.goalEndDate' })}
                                InputLabelProps={{ shrink: true }}
                                value={goal.endDate || ''}
                                onChange={(e) => updateParentGoal(idx, 'endDate', e.target.value)}
                                inputProps={{
                                  min: goal.startDate || iepStart || undefined,
                                  max: iepEnd || undefined
                                }}
                                helperText={
                                  iepWindowReady
                                    ? intl.formatMessage({ id: 'iep.goalDateBoundsHint' }, { start: iepStart, end: iepEnd })
                                    : ' '
                                }
                              />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                              <TextField
                                fullWidth
                                size="small"
                                select
                                label={intl.formatMessage({ id: 'iep.goalStatus' })}
                                value={goal.status || ''}
                                onChange={(e) => updateParentGoal(idx, 'status', e.target.value)}
                              >
                                <MenuItem value="">—</MenuItem>
                                {IEP_GOAL_STATUS_OPTIONS.map((s) => (
                                  <MenuItem key={s.value} value={s.value}>
                                    {intl.formatMessage({ id: s.labelKey })}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                              <TextField
                                fullWidth
                                size="small"
                                multiline
                                minRows={2}
                                label={intl.formatMessage({ id: 'iep.goalBaseline' })}
                                value={goal.baseline || ''}
                                onChange={(e) => updateParentGoal(idx, 'baseline', e.target.value)}
                              />
                            </Grid>
                          </Grid>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </Box>

                {/* Parent Training schedule — rich text */}
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                    {intl.formatMessage({ id: 'iep.parent.trainingScheduleTitle' })}
                  </Typography>
                  {importGapNote('parentTrainingSchedule')}
                  <RichTextField
                    value={formData.parentTrainingSchedule}
                    onChange={(v) => setField('parentTrainingSchedule', v)}
                    readOnly={readOnly}
                    minHeight={140}
                  />
                </Box>

                {/* Transition Plan — rich text */}
                <Box>
                  <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                    {intl.formatMessage({ id: 'iep.parent.transitionPlanTitle' })}
                  </Typography>
                  {importGapNote('transitionPlan')}
                  <RichTextField
                    value={formData.transitionPlan}
                    onChange={(v) => setField('transitionPlan', v)}
                    readOnly={readOnly}
                    minHeight={140}
                  />
                </Box>
              </Stack>
            );
          })()}
        </SectionPanel>

        {/* p14: Assessment Summary (rich text) — repurposes the legacy
            `summary` field on the form-data shape so older saves still
            populate this panel. Panel id stays new (p14) to read clearly. */}
        <SectionPanel
          panelId="p14"
          title={intl.formatMessage({ id: 'iep.section.assessmentSummary' })}
          subtitle={intl.formatMessage({ id: 'iep.section.assessmentSummaryDesc' })}
          expanded={expandedPanels.has('p14')}
          onToggle={togglePanel('p14')}
          theme={theme}
        >
          {importGapNote('summary')}
          <RichTextField value={formData.summary} onChange={(v) => setField('summary', v)} readOnly={readOnly} minHeight={180} />
        </SectionPanel>

        {/* p15: Recommendation (rich text) */}
        <SectionPanel
          panelId="p15"
          title={intl.formatMessage({ id: 'iep.section.recommendation' })}
          subtitle={intl.formatMessage({ id: 'iep.section.recommendationDesc' })}
          expanded={expandedPanels.has('p15')}
          onToggle={togglePanel('p15')}
          theme={theme}
        >
          {importGapNote('recommendation')}
          <RichTextField
            value={formData.recommendation}
            onChange={(v) => setField('recommendation', v)}
            readOnly={readOnly}
            minHeight={180}
          />
        </SectionPanel>

        {/* p12: Sign-off (with signature pads) */}
        <SectionPanel
          panelId="p12"
          title={intl.formatMessage({ id: 'iep.section.signoff' })}
          subtitle={intl.formatMessage({ id: 'iep.section.signoffDesc' })}
          expanded={expandedPanels.has('p12')}
          onToggle={togglePanel('p12')}
          theme={theme}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              {readOnly ? (
                <ReadOnlySignature label={intl.formatMessage({ id: 'iep.parentSignature' })} dataUrl={formData.parentSignature} />
              ) : (
                <SignaturePad
                  ref={parentSigRef}
                  label={intl.formatMessage({ id: 'iep.parentSignature' })}
                  signHereLabel={intl.formatMessage({ id: 'form.injuryReport.signHere' })}
                  clearLabel={intl.formatMessage({ id: 'form.injuryReport.clearSignature' })}
                  uploadLabel={intl.formatMessage({ id: 'form.injuryReport.uploadSignature' })}
                  initialDataUrl={formData.parentSignature}
                  onEnd={(data) => setField('parentSignature', data)}
                />
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              {readOnly ? (
                <ReadOnlySignature label={intl.formatMessage({ id: 'iep.supervisorSignature' })} dataUrl={formData.supervisorSignature} />
              ) : (
                <SignaturePad
                  ref={supervisorSigRef}
                  label={intl.formatMessage({ id: 'iep.supervisorSignature' })}
                  signHereLabel={intl.formatMessage({ id: 'form.injuryReport.signHere' })}
                  clearLabel={intl.formatMessage({ id: 'form.injuryReport.clearSignature' })}
                  uploadLabel={intl.formatMessage({ id: 'form.injuryReport.uploadSignature' })}
                  initialDataUrl={formData.supervisorSignature}
                  onEnd={(data) => setField('supervisorSignature', data)}
                />
              )}
            </Grid>
          </Grid>
        </SectionPanel>
      </Box>

      {/* Footer Actions — sticky action bar so Save stays reachable on this long
          form. Flat (no elevation) with just a top divider so it reads as the
          form's action bar, not a separate floating card. Hidden in read-only mode. */}
      {!readOnly && (
        <Paper
          elevation={0}
          square
          sx={{
            position: 'sticky',
            bottom: 0,
            zIndex: (t) => t.zIndex.appBar,
            py: 1.5,
            px: 2,
            borderTop: 1,
            borderColor: 'divider'
          }}
        >
          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button variant="outlined" onClick={handleCancel} disabled={saving}>
              {intl.formatMessage({ id: 'common.cancel' })}
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveOutlined />}
            >
              {intl.formatMessage({ id: saving ? 'common.saving' : 'iep.saveAndContinue' })}
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>

      <ImportIepDialog open={importOpen} onClose={() => setImportOpen(false)} onApply={handleImportApply} />

      {/* Skill Repository goal builder — goals only (targets are attached in
          the child's Goals & Targets tab). */}
      <GoalBuilderModal
        open={repoBuilderOpen}
        onClose={() => setRepoBuilderOpen(false)}
        onAdd={handleRepoGoalAdd}
        existingCodes={(formData.iepGoals || []).map((g) => g.goalId)}
      />

      {/* Free-form custom goal add/edit — shared with the Goals & Targets tab */}
      <GoalDetailsModal
        open={Boolean(customGoalModal)}
        onClose={() => setCustomGoalModal(null)}
        onSave={handleCustomGoalSave}
        initial={customGoalModal?.editIdx != null ? formData.iepGoals[customGoalModal.editIdx] : null}
        domainOptions={domainOptions}
        statusOptions={IEP_GOAL_STATUS_OPTIONS.map((s) => ({ value: s.value, label: intl.formatMessage({ id: s.labelKey }) }))}
        nextCode={customGoalModal?.editIdx == null ? nextSuggestedGoalId(formData.iepGoals) : ''}
      />

      {/* Delete-goal confirmation */}
      <Dialog open={goalDeleteIdx != null} onClose={() => setGoalDeleteIdx(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{intl.formatMessage({ id: 'iep.deleteGoalTitle' })}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {intl.formatMessage(
              { id: 'iep.deleteGoalMessage' },
              {
                name: (goalDeleteIdx != null && (formData.iepGoals[goalDeleteIdx]?.name || formData.iepGoals[goalDeleteIdx]?.goalId)) || ''
              }
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGoalDeleteIdx(null)}>{intl.formatMessage({ id: 'common.cancel' })}</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              const idx = goalDeleteIdx;
              setGoalDeleteIdx(null);
              if (idx != null) removeIepGoal(idx);
            }}
          >
            {intl.formatMessage({ id: 'common.delete' })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add / Edit behavior of concern — shared modal (also used by the
          Tracked Behaviors tab). */}
      <BehaviorEditorDialog
        open={Boolean(behaviorModal)}
        initial={behaviorModal?.editIdx != null ? formData.behaviors[behaviorModal.editIdx] : null}
        existingNames={otherBehaviorNames(formData.behaviors, behaviorModal?.editIdx ?? null)}
        onClose={() => setBehaviorModal(null)}
        onSave={handleBehaviorSave}
      />

      {/* View behavior of concern (read-only IEP view) */}
      <BehaviorEditorDialog
        open={behaviorViewIdx != null}
        initial={behaviorViewIdx != null ? formData.behaviors[behaviorViewIdx] : null}
        readOnly
        onClose={() => setBehaviorViewIdx(null)}
        onSave={() => setBehaviorViewIdx(null)}
      />

      {/* Delete-behavior confirmation */}
      <Dialog open={behaviorDeleteIdx != null} onClose={() => setBehaviorDeleteIdx(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{intl.formatMessage({ id: 'behaviorsTab.deleteBehaviorTitle' })}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {intl.formatMessage(
              { id: 'behaviorsTab.deleteBehaviorConfirm' },
              {
                name:
                  (behaviorDeleteIdx != null && formData.behaviors[behaviorDeleteIdx]?.behaviorName) ||
                  intl.formatMessage({ id: 'behaviorsTab.unnamed' })
              }
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBehaviorDeleteIdx(null)}>{intl.formatMessage({ id: 'common.cancel' })}</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              const idx = behaviorDeleteIdx;
              setBehaviorDeleteIdx(null);
              if (idx != null) removeBehaviorAt(idx);
            }}
          >
            {intl.formatMessage({ id: 'common.delete' })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unsaved-changes prompt for in-app navigation */}
      <Dialog open={blocker?.state === 'blocked'} onClose={() => blocker.reset?.()} maxWidth="xs" fullWidth>
        <DialogTitle>{intl.formatMessage({ id: 'common.leaveTitle' })}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{intl.formatMessage({ id: 'common.leaveBody' })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => blocker.reset?.()}>{intl.formatMessage({ id: 'common.leaveStay' })}</Button>
          <Button color="error" variant="contained" onClick={() => blocker.proceed?.()}>
            {intl.formatMessage({ id: 'common.leaveDiscard' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
